import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { useCurrency } from '../../hooks/useCurrency';
import PageAnimationWrapper from '../../components/PageAnimationWrapper';
import { openRazorpayCheckout } from '../../utils/razorpayCheckout';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Clock,
  Users,
  Eye,
  Building2,
  Target,
  Lightbulb,
  X,
  FolderOpen,
  Info,
  HelpCircle,
  ChevronRight,
  Search,
  BarChart3,
  Calendar,
  TrendingUp,
  Activity,
  User,
  Briefcase,
  Brain,
  Sparkles,
  AlertTriangle,
  ArrowRight,
  CreditCard,
  Crown,
  Zap,
  Check,
  LogOut,
  Filter,
  Download,
  LineChart,
  CheckCircle,
  TrendingDown,
  Flame,
} from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { AntRangePicker, AntDropdown } from '../../components/UI';
import { Modal, Card, Button, Typography, Space, Divider, Badge, Row, Col, Statistic, Skeleton, InputNumber, Tooltip } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import ProjectMemberHoursSidebar from '../../components/ProjectMemberHoursSidebar';
import MemberProjectAnalyticsSidebar from '../../components/MemberProjectAnalyticsSidebar';
import IdleLogsModal from '../../components/IdleLogsModal/IdleLogsModal';
import GoalsLeaderboardCard from '../../components/GoalsLeaderboardCard';
import { AnimatedNumber } from '../../components/AnimatedNumber';
import { AnimatedCard, cardVariants, cardHoverVariants } from '../../utils/animations';
import Chart from 'react-apexcharts';
import { getTeamStats } from '../../services/goalsPerformanceApi';
import { isS5Organisation } from '../../utils/organization';
import OrgGmailStatsCard from '../../modules/email-insights/components/OrgGmailStatsCard';

const { Title, Text, Paragraph } = Typography;

// Helper functions for date comparison
const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.toDateString() === date2.toDateString();
};

const getYesterday = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday;
};

const getDaysDifference = (start, end) => {
  if (!start || !end) return -1;
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show max 7 page numbers at a time

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 4) {
        // Near the start: show 1, 2, 3, 4, 5, ..., last
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Near the end: show 1, ..., last-4, last-3, last-2, last-1, last
        pages.push('ellipsis');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle: show 1, ..., current-1, current, current+1, ..., last
        pages.push('ellipsis');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
      <div className="text-gray-400 text-sm">
        Showing {startItem}-{endItem} of {totalItems} items
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 text-sm bg-white/10 hover:bg-white/20 disabled:bg-white/5 disabled:text-gray-500 disabled:cursor-not-allowed rounded-md transition-colors"
        >
          Previous
        </button>
        <div className="flex items-center space-x-1">
          {pageNumbers.map((page, index) => {
            if (page === 'ellipsis') {
              return (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                  ...
                </span>
              );
            }
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${page === currentPage
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
              >
                {page}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 text-sm bg-white/10 hover:bg-white/20 disabled:bg-white/5 disabled:text-gray-500 disabled:cursor-not-allowed rounded-md transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

// Utility function to format date without timezone issues
const formatDateForAPI = (date, isEndDate = false) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  if (isEndDate) {
    // For end date, include time to cover the entire day
    return `${year}-${month}-${day}T23:59:59.999Z`;
  } else {
    // For start date, use beginning of day
    return `${year}-${month}-${day}T00:00:00.000Z`;
  }
};

// Custom styles for dark theme datepicker
const customDatepickerStyles = `
  .react-datepicker {
    background-color: rgba(0, 0, 0, 0.95) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 24px !important;
    font-family: inherit !important;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8) !important;
    backdrop-filter: blur(20px) !important;
  }
  
  .react-datepicker__header {
    background-color: rgba(0, 0, 0, 0.8) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 24px 24px 0 0 !important;
    padding: 16px 0 12px 0 !important;
  }
  
  .react-datepicker__current-month {
    color: #ffffff !important;
    font-weight: 600 !important;
    font-size: 16px !important;
    margin-bottom: 8px !important;
  }
  
  .react-datepicker__day-name {
    color: rgba(255, 255, 255, 0.6) !important;
    font-weight: 500 !important;
    font-size: 12px !important;
    padding: 8px 0 !important;
  }
  
  .react-datepicker__day {
    color: #ffffff !important;
    border-radius: 12px !important;
    margin: 2px !important;
    width: 32px !important;
    height: 32px !important;
    line-height: 32px !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    transition: all 0.2s ease !important;
  }
  
  .react-datepicker__day:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: #ffffff !important;
    transform: scale(1.05) !important;
  }
  
  .react-datepicker__day--selected {
    background-color: rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    font-weight: 600 !important;
  }
  
  .react-datepicker__day--in-range {
    background-color: rgba(59, 130, 246, 0.8) !important;
    color: #ffffff !important;
    font-weight: 600 !important;
  }
  
  .react-datepicker__day--keyboard-selected {
    background-color: rgba(59, 130, 246, 0.6) !important;
    color: #ffffff !important;
  }
  
  .react-datepicker__day--in-selecting-range {
    background-color: rgba(59, 130, 246, 0.4) !important;
    color: #ffffff !important;
  }
  
  .react-datepicker__day--outside-month {
    color: rgba(255, 255, 255, 0.3) !important;
  }
  
  .react-datepicker__navigation {
    color: rgba(255, 255, 255, 0.8) !important;
    border-radius: 12px !important;
    width: 32px !important;
    height: 32px !important;
    transition: all 0.2s ease !important;
  }
  
  .react-datepicker__navigation:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: #ffffff !important;
  }
  
  .react-datepicker__month-container {
    background-color: transparent !important;
    padding: 8px !important;
  }
  
  .react-datepicker__input-container input {
    background-color: rgba(0, 0, 0, 0.5) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 16px !important;
    padding: 12px 16px !important;
    color: #ffffff !important;
    font-size: 14px !important;
    width: 100% !important;
    outline: none !important;
    transition: all 0.3s ease !important;
    font-weight: 500 !important;
  }
  
  .react-datepicker__input-container input:focus {
    border-color: rgba(255, 255, 255, 0.4) !important;
    background-color: rgba(0, 0, 0, 0.7) !important;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1) !important;
  }
  
  .react-datepicker__input-container input::placeholder {
    color: rgba(255, 255, 255, 0.5) !important;
  }
  
  .react-datepicker__close-icon {
    background-color: rgba(255, 255, 255, 0.1) !important;
    border-radius: 50% !important;
    width: 20px !important;
    height: 20px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: all 0.2s ease !important;
  }
  
  .react-datepicker__close-icon:hover {
    background-color: rgba(255, 255, 255, 0.2) !important;
  }
  
  .react-datepicker__close-icon::after {
    color: rgba(255, 255, 255, 0.8) !important;
    font-size: 14px !important;
    font-weight: bold !important;
  }
  
  .react-datepicker__triangle {
    display: none !important;
  }
  
  .react-datepicker__month {
    margin: 0 !important;
  }
  
  .react-datepicker__week {
    margin: 2px 0 !important;
  }
  
  .datepicker-popper {
    z-index: 99999 !important;
  }
  
  .datepicker-popper[data-popper-placement^="bottom"] {
    margin-top: 12px !important;
  }
  
  .datepicker-popper[data-popper-placement^="top"] {
    margin-bottom: 12px !important;
  }
  
  .react-datepicker__input-container {
    position: relative !important;
  }
  
  .react-datepicker__input-container input {
    cursor: pointer !important;
    width: 100% !important;
    min-width: 280px !important;
  }
  
  .react-datepicker__input-container input:hover {
    border-color: rgba(255, 255, 255, 0.3) !important;
  }
  
  .react-datepicker__day--today {
    background-color: rgba(255, 255, 255, 0.05) !important;
    color: #ffffff !important;
    font-weight: 600 !important;
  }
  
  .react-datepicker__day--today:hover {
    background-color: rgba(255, 255, 255, 0.15) !important;
  }
  
  /* Ensure popup positioning */
  .react-datepicker-popper {
    position: absolute !important;
    z-index: 99999 !important;
  }
  
  /* Prevent popup from being cut off */
  .react-datepicker-wrapper {
    width: 100% !important;
  }
  
  /* Ensure the calendar container has proper dimensions */
  .react-datepicker {
    min-width: 320px !important;
    max-width: 400px !important;
  }
  
  /* Better spacing for the date range section */
  .date-range-section {
    margin-bottom: 24px !important;
  }
  
  /* Force popup to stay above other elements */
  .react-datepicker__triangle {
    display: none !important;
  }
  
  /* Ensure proper stacking context */
  .react-datepicker-popper[data-popper-placement] {
    z-index: 99999 !important;
  }
  
  /* Additional positioning fixes */
  .react-datepicker-popper {
    position: absolute !important;
    z-index: 99999 !important;
    transform: none !important;
  }
  
  /* Ensure the popper stays within viewport */
  .react-datepicker-popper[data-popper-placement*="bottom"] {
    top: 100% !important;
    margin-top: 8px !important;
  }
  
  /* Ensure input container is properly sized */
  .react-datepicker__input-container {
    width: 100% !important;
    display: block !important;
  }
  
  /* Fix for any positioning issues */
  .react-datepicker-wrapper {
    width: 100% !important;
    display: block !important;
  }

  /* Custom Scrollbar Styles */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  }
  
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    transition: background 0.2s ease;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  .scrollbar-thumb-white-20::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
  }
  
  .scrollbar-track-transparent::-webkit-scrollbar-track {
    background: transparent;
  }
`;

// Custom Dropdown Component (matching DailySummary style)
const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  className = "",
  currentUserId = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);

  const filteredOptions = options.filter((option) => {
    const displayName = option.name ||
      `${option.firstName || ""} ${option.lastName || ""}`.trim();
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const selectedOption = options.find((option) => option._id === value);

  const getDisplayName = (option) => {
    return (
      option.name || `${option.firstName || ""} ${option.lastName || ""}`.trim()
    );
  };

  const getPlaceholderText = () => {
    if (placeholder.includes("team")) return "All Team Members";
    return placeholder;
  };

  // Calculate dropdown position
  const updateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();
      const handleScroll = () => updateDropdownPosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest(".dropdown-container") && !event.target.closest('[data-dropdown-portal]')) {
        console.log('Clicking outside dropdown, closing...');
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative dropdown-container ${className}`}>
      <div
        ref={triggerRef}
        className="w-full px-2 py-1.5 bg-white/5 border border-white/30 rounded-md focus-within:ring-2 focus-within:ring-white/20 focus-within:border-white/20 text-white cursor-pointer text-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <span className={selectedOption ? "text-white" : "text-gray-400"}>
            {selectedOption
              ? getDisplayName(selectedOption)
              : getPlaceholderText()}
          </span>
          <ChevronRight
            className={`h-3 w-3 text-gray-400 transition-transform ${isOpen ? "rotate-90" : ""
              }`}
          />
        </div>
      </div>

      {isOpen && createPortal(
        <div
          data-dropdown-portal
          className="fixed bg-gray-900 border border-white/30 rounded-md shadow-2xl max-h-60 overflow-hidden"
          style={{
            zIndex: 999999,
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            minWidth: '200px'
          }}
        >
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3 w-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-7 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20 text-white placeholder-gray-400 text-xs"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            <div
              className="px-3 py-1.5 text-gray-400 hover:bg-white/10 cursor-pointer text-xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('All Team Members clicked');
                onChange("");
                // Small delay to ensure state update happens before closing
                setTimeout(() => {
                  setIsOpen(false);
                  setSearchTerm("");
                }, 10);
              }}
            >
              {getPlaceholderText()}
            </div>
            {filteredOptions.map((option) => (
              <div
                key={option._id || option.id}
                className="px-3 py-1.5 text-white hover:bg-white/10 cursor-pointer text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Dropdown option clicked:', option._id || option.id, option);
                  onChange(option._id || option.id);
                  // Small delay to ensure state update happens before closing
                  setTimeout(() => {
                    setIsOpen(false);
                    setSearchTerm("");
                  }, 10);
                }}
              >
                {getDisplayName(option)}
                {option._id === currentUserId ? ' (You)' :
                  option.reportingManager === currentUserId ? ' (Direct)' : ' (Team Member)'}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Feature flag for Team Performance export - default: true (visible)
const ENABLE_TEAM_PERFORMANCE_EXPORT = true;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const api = useApi();
  const [billingCurrency, setBillingCurrency] = useState(null);
  const { formatPrice, getPrice, symbol } = useCurrency({ billingCurrency });
  const isS5 = isS5Organisation(user);
  const navigate = useNavigate();
  const { mode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date()); // For daily countdown update
  const [hoursData, setHoursData] = useState({
    totalHours: '0h 0m',
    totalSessions: 0,
    idleTime: '0h 0m',
    activeTime: '0h 0m'
  });

  // Filter states
  const [selectedTeamMember, setSelectedTeamMember] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // Initialize with September 3rd where we have actual data
  // const dataDate = new Date('2025-09-03'); // Date with actual daily summary data
  const today = new Date();
  const [dateRange, setDateRange] = useState([today, today]);
  // const [dateRange, setDateRange] = useState([dataDate, dataDate]);
  const [startDate, endDate] = dateRange;

  // Team members data
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamStats, setTeamStats] = useState({
    totalMembers: 0,
    directReports: 0,
    indirectReports: 0,
    activeMembers: 0
  });

  // Theme state
  const [theme, setTheme] = useState('dark');
  const isDark = (mode ?? theme) === 'dark';

  // Productivity and Projects data
  const [productivityData, setProductivityData] = useState({
    myScore: 0,
    teamAverage: 0,
    overallScore: 0
  });

  const [teamAverageData, setTeamAverageData] = useState({
    teamAverage: 0,
    userScore: 0,
    teamMembersCount: 0,
    validScoresCount: 0
  });

  const [projectsData, setProjectsData] = useState({
    myProjects: 0,
    directTeamProjects: 0,
    indirectTeamProjects: 0,
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    archivedProjects: 0
  });

  // Goals team stats
  const [goalsTeamStats, setGoalsTeamStats] = useState({
    teamAverageScore: null,
    completedGoalsCount: 0,
    totalGoalsCount: 0,
    previousMonthAverageScore: null,
    previousMonthChange: null
  });
  const [goalsTeamStatsLoading, setGoalsTeamStatsLoading] = useState(false);

  // Team Analytics data
  const [teamAnalytics, setTeamAnalytics] = useState([]);
  const [teamAnalyticsLoading, setTeamAnalyticsLoading] = useState(false);
  const [teamAnalyticsPagination, setTeamAnalyticsPagination] = useState({
    total: 0,
    pages: 0,
    page: 1
  });

  // Project Overview data
  const [projectOverview, setProjectOverview] = useState([]);
  const [projectOverviewPagination, setProjectOverviewPagination] = useState({
    total: 0,
    pages: 0,
    page: 1
  });

  // Payment success handling
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState(''); // Coupon code for checkout
  const [actualCouponId, setActualCouponId] = useState(null); // Actual coupon ID from Stripe
  const [couponValid, setCouponValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [couponVerifying, setCouponVerifying] = useState(false); // Loading state for verification
  const [couponDiscount, setCouponDiscount] = useState(null); // Discount info
  const couponTimeoutRef = useRef(null); // For debouncing coupon verification
  const [selectedPlanForRenewal, setSelectedPlanForRenewal] = useState(null); // Selected plan in renewal modal
  const [allProjects, setAllProjects] = useState([]);
  const [projectOverviewLoading, setProjectOverviewLoading] = useState(false);
  const [projectStats, setProjectStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    archivedProjects: 0,
    totalTimeSpent: '0h 0m',
    averageSessionTime: '0h 0m',
    teamMembers: 0
  });

  // Pagination states
  const [projectsPage, setProjectsPage] = useState(1);
  const [teamPage, setTeamPage] = useState(1);
  const itemsPerPage = 5;

  // Pagination helper functions
  const getPaginatedData = (data, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = (data, itemsPerPage) => {
    return Math.ceil(data.length / itemsPerPage);
  };

  const resetPagination = () => {
    setProjectsPage(1);
    setTeamPage(1);
  };

  // Idle Logs Modal state
  const [showIdleLogsModal, setShowIdleLogsModal] = useState(false);
  const [idleLogsTarget, setIdleLogsTarget] = useState({ userId: null, memberName: null });

  const openIdleLogs = useCallback((userId = null, memberName = null) => {
    setIdleLogsTarget({ userId, memberName });
    setShowIdleLogsModal(true);
  }, []);

  // Modal states
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);

  // Member Hours Sidebar states (Project Analysis of Team Member)
  const [showMemberHoursModal, setShowMemberHoursModal] = useState(false);
  const [isMemberHoursPanelAnimating, setIsMemberHoursPanelAnimating] = useState(false);
  const [selectedProjectForAnalysis, setSelectedProjectForAnalysis] = useState(null);

  // Member Project Analytics Sidebar states
  const [showMemberAnalyticsSidebar, setShowMemberAnalyticsSidebar] = useState(false);
  const [isMemberAnalyticsPanelAnimating, setIsMemberAnalyticsPanelAnimating] = useState(false);
  const [selectedMemberForAnalytics, setSelectedMemberForAnalytics] = useState(null);

  // Sort states for All Projects and Team Performance
  const [projectsSortOrder, setProjectsSortOrder] = useState('desc'); // Default descending (most time first)
  const [teamSortOrder, setTeamSortOrder] = useState('desc'); // Default descending (Z→A)

  // Search states for modals
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [projectsSearchTerm, setProjectsSearchTerm] = useState('');

  // Computed sorted and filtered data
  const sortedAllProjects = useMemo(() => {
    if (!allProjects || allProjects.length === 0) return [];

    // First filter by search term
    const filteredProjects = allProjects.filter(project => {
      if (!projectsSearchTerm) return true;
      const searchLower = projectsSearchTerm.toLowerCase();
      const projectName = (project.name || project.projectName || '').toLowerCase();
      const description = (project.description || '').toLowerCase();
      return projectName.includes(searchLower) || description.includes(searchLower);
    });

    // Add productivity calculation to each project
    const projectsWithProductivity = filteredProjects.map(project => {
      // Calculate productivity based on teamAnalytics data
      let productivity = 0;

      if (teamAnalytics && teamAnalytics.length > 0 && project.teamMembers) {
        // Get team members for this project
        const projectMemberIds = Array.isArray(project.teamMembers)
          ? project.teamMembers.map(m => m._id || m.userId || m.id || m)
          : [];

        if (projectMemberIds.length > 0) {
          // Find productivity scores for members working on this project
          const memberScores = teamAnalytics
            .filter(member => projectMemberIds.includes(member.userId || member._id))
            .map(member => member.productivity || 0);

          // Calculate average productivity
          if (memberScores.length > 0) {
            productivity = Math.round(memberScores.reduce((sum, score) => sum + score, 0) / memberScores.length);
          }
        }
      }

      // If no team analytics, try to calculate from project status
      if (productivity === 0 && project.status === 'active') {
        // For active projects with time spent, estimate productivity
        const timeStr = project.timeSpent || project.formattedTime || '0h 0m';
        const match = timeStr.match(/(\d+)h/);
        if (match) {
          const hours = parseInt(match[1]) || 0;
          // Simple heuristic: more hours = higher productivity for active projects
          productivity = Math.min(Math.round(hours * 10), 100);
        }
      }

      return {
        ...project,
        productivity
      };
    });

    // Sorting is now handled by the backend API
    return [...projectsWithProductivity];
  }, [allProjects, projectsSearchTerm, teamAnalytics]);

  const sortedProjectOverview = useMemo(() => {
    if (!projectOverview || projectOverview.length === 0) return [];

    return [...projectOverview]; // Sorting is now handled by the backend API
  }, [projectOverview]);

  const sortedTeamAnalytics = useMemo(() => {
    if (!teamAnalytics || teamAnalytics.length === 0) return [];

    // If a team member is selected, show them first, then only their reports
    let filteredTeam = teamAnalytics;
    if (selectedTeamMember) {
      // Find the selected member
      const selectedMember = teamAnalytics.find(member => {
        return member.userId === selectedTeamMember || member._id === selectedTeamMember;
      });

      // Get all reportees (direct and indirect) recursively
      // Use teamMembers data for hierarchy, but filter teamAnalytics for display
      const getAllReportees = (managerId, allMembers) => {
        const directReports = allMembers.filter(member => {
          // Check if member reports to the manager
          const reportsToManager = member.reportingManager &&
            (member.reportingManager._id === managerId || member.reportingManager.id === managerId);

          return reportsToManager && (member._id !== selectedTeamMember);
        });

        let allReportees = [...directReports];

        // Recursively get indirect reports
        directReports.forEach(report => {
          const reportId = report._id;
          const indirectReports = getAllReportees(reportId, allMembers);
          allReportees = [...allReportees, ...indirectReports];
        });

        return allReportees;
      };

      // Get reportee IDs from teamMembers data (which has hierarchy)
      const reporteeIds = getAllReportees(selectedTeamMember, teamMembers).map(r => r._id);

      // Filter teamAnalytics to show only the reportees
      const reports = teamAnalytics.filter(member => {
        const memberId = member.userId || member._id;
        return reporteeIds.includes(memberId);
      });

      // Debug logging
      console.log('🔍 Hierarchy Debug:', {
        selectedTeamMember,
        selectedMember: selectedMember ? `${selectedMember.firstName} ${selectedMember.lastName}` : 'Not found',
        selectedMemberData: selectedMember,
        reporteeIds,
        totalReports: reports.length,
        reports: reports.map(r => ({
          name: `${r.firstName} ${r.lastName}`,
          id: r.userId || r._id,
          email: r.email
        })),
        teamMembersData: teamMembers.map(m => ({
          name: `${m.firstName} ${m.lastName}`,
          id: m._id,
          reportingManager: m.reportingManager ? {
            id: m.reportingManager._id || m.reportingManager.id,
            name: m.reportingManager.firstName + ' ' + m.reportingManager.lastName
          } : null,
          email: m.email
        })),
        teamAnalyticsData: teamAnalytics.map(m => ({
          name: `${m.firstName} ${m.lastName}`,
          id: m.userId || m._id,
          email: m.email,
          allFields: Object.keys(m)
        }))
      });

      // Special check for Mahima's reportees
      if (selectedTeamMember === '68b4767871abba8df30c4198') {
        console.log('🔍 Mahima Special Debug:', {
          mahimaInTeamMembers: teamMembers.find(m => m._id === '68b4767871abba8df30c4198'),
          mahimaInTeamAnalytics: teamAnalytics.find(m => (m.userId || m._id) === '68b4767871abba8df30c4198'),
          allMembersReportingToMahima: teamMembers.filter(m =>
            m.reportingManager && (m.reportingManager._id === '68b4767871abba8df30c4198' || m.reportingManager.id === '68b4767871abba8df30c4198')
          ),
          allAnalyticsWithMahimaId: teamAnalytics.filter(m => (m.userId || m._id) === '68b4767871abba8df30c4198')
        });
      }

      // Combine: selected member first, then their reports
      filteredTeam = selectedMember ? [selectedMember, ...reports] : reports;
    }

    // Then filter by search term
    filteredTeam = filteredTeam.filter(member => {
      if (!teamSearchTerm) return true;
      const searchLower = teamSearchTerm.toLowerCase();
      const fullName = `${member.firstName || ''} ${member.lastName || ''}`.toLowerCase();
      const email = (member.email || '').toLowerCase();
      const position = (member.position || '').toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower) || position.includes(searchLower);
    });

    // Helper function to parse time strings like "5h 30m" to minutes
    const parseTimeToMinutes = (timeStr) => {
      if (!timeStr || typeof timeStr !== 'string') return 0;

      const match = timeStr.match(/(\d+)h\s*(\d+)m?/);
      if (match) {
        const hours = parseInt(match[1]) || 0;
        const minutes = parseInt(match[2]) || 0;
        return hours * 60 + minutes;
      }

      // Fallback: try to extract numbers
      const numbers = timeStr.match(/\d+/g);
      if (numbers && numbers.length >= 1) {
        const firstNum = parseInt(numbers[0]) || 0;
        const secondNum = numbers.length > 1 ? parseInt(numbers[1]) || 0 : 0;
        return firstNum * 60 + secondNum;
      }

      return 0;
    };

    // Sorting is now handled by the backend API
    return [...filteredTeam];
  }, [teamAnalytics, teamSearchTerm, selectedTeamMember]);

  // Most Visited data
  const [mostVisited, setMostVisited] = useState([]);
  const [mostVisitedLoading, setMostVisitedLoading] = useState(false);
  const [mostVisitedDebug, setMostVisitedDebug] = useState(null);

  // Server-side pagination for most visited
  const [mostVisitedPage, setMostVisitedPage] = useState(1);
  const [mostVisitedLimit] = useState(5);
  const [mostVisitedTotal, setMostVisitedTotal] = useState(0);
  const [mostVisitedHasMore, setMostVisitedHasMore] = useState(false);

  // Reset pagination when filters change
  useEffect(() => {
    setMostVisitedPage(1);
  }, [startDate, endDate, selectedTeamMember]);

  // Load organization for restriction check
  const [organization, setOrganization] = useState(null);
  const [orgLoaded, setOrgLoaded] = useState(false);

  // Function to load organization data (can be called from anywhere)
  const loadOrganization = useCallback(async (forceRefresh = false) => {
    // Clear cache if force refresh
    if (forceRefresh && typeof window !== 'undefined') {
      try {
        delete window.__orgProfile;
        delete window.__orgProfileFetchedAt;
      } catch (_) { }
    }

    // Use cached profile if present and not forcing refresh
    // BUT: Always fetch fresh data if bypass is enabled (to get latest status)
    const cachedData = typeof window !== 'undefined' ? window.__orgProfile : null;
    if (!forceRefresh && cachedData && !cachedData.paymentBypassEnabled) {
      setOrganization(cachedData);
      setOrgLoaded(true);
      return;
    }

    try {
      const resp = await api.get('/api/v1/billing/profile');
      if (resp.data?.success) {
        const orgData = resp.data.data;
        console.log('🔍 [DASHBOARD] Organization data loaded:', {
          paymentBypassEnabled: orgData.paymentBypassEnabled,
          orgPlanSelection: orgData.orgPlanSelection,
          subscriptionStatus: orgData.subscriptionStatus,
          userIsActivated: orgData.userIsActivated,
          userPaymentRequired: orgData.userPaymentRequired,
          userSubscriptionStatus: orgData.userSubscriptionStatus,
          fullData: orgData
        });
        setOrganization(orgData);
        setBillingCurrency(orgData.orgPlanCurrency || (orgData.paymentGateway === 'razorpay' ? 'INR' : 'USD'));
        try {
          window.__orgProfile = orgData;
          window.__orgProfileFetchedAt = Date.now();
        } catch (_) { }
      }
    } catch (error) {
      console.error('Error loading organization:', error);
    } finally {
      setOrgLoaded(true);
    }
  }, []); // Removed 'api' - it's stable via useMemo in useApi hook, accessed via closure

  // Initial load organization
  useEffect(() => {
    loadOrganization(false);
  }, [loadOrganization]);

  // Update current time every hour to refresh trial countdown daily
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60 * 60 * 1000); // Update every hour

    return () => clearInterval(interval);
  }, []);

  // Handle payment success - use ref to prevent multiple executions
  const paymentProcessedRef = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const userId = urlParams.get('userId');
    const plan = urlParams.get('plan');
    const orgId = urlParams.get('orgId');

    // Prevent multiple executions
    if (payment === 'success' && !paymentProcessedRef.current) {
      paymentProcessedRef.current = true;
      // For organization subscription, we have orgId
      // For user subscription, we have userId and plan
      if (orgId) {
        // Organization-level subscription payment
        setPaymentSuccess(true);
        setSelectedPlan(plan || 'organization');

        // Clear all caches aggressively
        if (typeof window !== 'undefined') {
          try {
            delete window.__orgProfile;
            delete window.__orgProfileFetchedAt;
          } catch (_) { }
        }

        // Force refresh organization data to get updated subscription status
        // Wrap async code in an async function
        (async () => {
          const syncBoth = async () => {
            try { await api.post('/api/v1/billing/sync-subscription'); } catch (_) {}
            try { await api.post('/api/v1/razorpay-billing/sync-subscription'); } catch (_) {}
          };

          await syncBoth();
          console.log('✅ Manual subscription sync completed');

          setTimeout(async () => {
            await loadOrganization(true);
            await syncBoth();

            setTimeout(async () => {
              await loadOrganization(true);
              await syncBoth();

              setTimeout(async () => {
                await loadOrganization(true);
                await syncBoth();

                // Clean URL after refresh
                window.history.replaceState({}, document.title, '/dashboard');

                // Show success toast only once (using toast id to prevent duplicates)
                toast.success('Subscription renewed successfully!', { id: 'subscription-renewed' });
              }, 3000);
            }, 3000);
          }, 2000);
        })();
      } else if (userId && plan) {
        // User-level subscription payment
        setPaymentSuccess(true);
        setSelectedPlan(plan);

        // Auto-login the user
        const autoLogin = async () => {
          try {
            const response = await api.post('/api/v1/auth/payment-login', {
              userId: userId,
              plan: plan
            });

            if (response.data.success) {
              // Store tokens
              localStorage.setItem('token', response.data.data.token);
              localStorage.setItem('refreshToken', response.data.data.refreshToken);

              // Clear all caches
              if (typeof window !== 'undefined') {
                try {
                  delete window.__orgProfile;
                  delete window.__orgProfileFetchedAt;
                } catch (_) { }
              }

              // Force refresh organization data with delay for webhook processing
              setTimeout(async () => {
                await loadOrganization(true);
                // Force another refresh to ensure data is synced
                setTimeout(async () => {
                  await loadOrganization(true);
                }, 2000);
              }, 1000);

              // Show success message
              toast.success(`Payment successful! Welcome to ${plan} plan!`);

              // Clean URL
              window.history.replaceState({}, document.title, '/dashboard');
            }
          } catch (error) {
            console.error('Auto-login failed:', error);
          }
        };

        autoLogin();
      }
    }

    // Cleanup: Reset ref when URL changes (but only if payment param is removed)
    return () => {
      const urlParams = new URLSearchParams(window.location.search);
      const payment = urlParams.get('payment');
      if (!payment || payment !== 'success') {
        // Only reset if payment param is not in URL anymore
        paymentProcessedRef.current = false;
      }
    };
  }, [api, loadOrganization]);

  // Compute restricted mode from billing profile
  // Checks both user-level AND organization-level subscription expiry
  const restricted = useMemo(() => {
    if (!organization) return false;

    // Check if organization has payment bypass enabled - if yes, no restrictions
    if (organization.paymentBypassEnabled === true) {
      console.log('✅ [DASHBOARD RESTRICTED] Payment bypass enabled - no restrictions', {
        paymentBypassEnabled: organization.paymentBypassEnabled,
        orgPlanSelection: organization.orgPlanSelection
      });
      return false; // No restrictions if bypass enabled
    }

    console.log('⚠️ [DASHBOARD RESTRICTED] Payment bypass check:', {
      paymentBypassEnabled: organization.paymentBypassEnabled,
      orgPlanSelection: organization.orgPlanSelection,
      subscriptionStatus: organization.subscriptionStatus,
      userSubscriptionStatus: organization.userSubscriptionStatus,
      userIsActivated: organization.userIsActivated
    });

    const now = new Date();
    // If bypass enabled but orgPlanSelection is null, treat as premium (no restrictions)
    const effectivePlan = organization.paymentBypassEnabled ? 'premium' : organization.orgPlanSelection;
    const isFree = effectivePlan === 'free';

    // User-level subscription check
    const userStatus = organization.userSubscriptionStatus;
    const userEndDate = organization.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
    const freeExpired = isFree && userEndDate && now > userEndDate;
    const userPaidInvalid = !isFree && (
      ['past_due', 'canceled', 'incomplete_expired', 'unpaid', 'expired'].includes(userStatus) ||
      (userEndDate && now > userEndDate)
    );

    // Organization-level subscription check (for all members)
    const orgStatus = organization.subscriptionStatus; // Organization-level subscription status
    const orgPeriodEnd = organization.currentPeriodEnd ? new Date(organization.currentPeriodEnd) : null;
    // Fallback to trialEnd if currentPeriodEnd is not set and status is trialing
    const effectivePeriodEnd = orgPeriodEnd || (orgStatus === 'trialing' && organization.trialEnd ? new Date(organization.trialEnd) : null);

    // Check if subscription is actually active
    // Active means: periodEnd is in the future (not expired)
    const hasActiveSubscription = effectivePeriodEnd && now < effectivePeriodEnd;

    // Check if subscription is expired
    // IMPORTANT: If periodEnd is in future, subscription is active regardless of status
    // This handles the case where webhook hasn't updated status yet but periodEnd is updated
    const orgExpired = !isFree && !hasActiveSubscription && (
      ['past_due', 'canceled', 'incomplete_expired', 'unpaid', 'expired'].includes(orgStatus) ||
      (effectivePeriodEnd && now > effectivePeriodEnd) ||
      !effectivePeriodEnd // No period end means expired
    );

    // If organization subscription expired, restrict ALL members
    const isRestricted = Boolean(freeExpired || userPaidInvalid || orgExpired);

    console.log('🔍 [DASHBOARD RESTRICTED] Final restricted check:', {
      restricted: isRestricted,
      freeExpired,
      userPaidInvalid,
      orgExpired,
      isFree,
      userStatus,
      orgStatus,
      userEndDate,
      orgPeriodEnd: orgPeriodEnd ? orgPeriodEnd.toISOString() : null,
      effectivePeriodEnd: effectivePeriodEnd ? effectivePeriodEnd.toISOString() : null,
      orgPeriodEndTimestamp: orgPeriodEnd ? orgPeriodEnd.getTime() : null,
      nowTimestamp: now.getTime(),
      periodEndInFuture: effectivePeriodEnd ? effectivePeriodEnd.getTime() > now.getTime() : false,
      hasActiveSubscription,
      stripeSubscriptionId: organization.stripeSubscriptionId,
      orgPlanSelection: organization.orgPlanSelection
    });

    return isRestricted;
  }, [organization]);

  // Calculate Free Trial Days Remaining
  // IMPORTANT: Only use organization-level data, ignore user-level subscription data
  // Banner should show same for both org_admin and org_user based on organization plan
  const trialDaysRemaining = useMemo(() => {
    // Don't show if organization data is not loaded
    if (!organization) {
      return null;
    }
    
    // Get organization-level plan and subscription status (ignore user-level data)
    const orgPlan = organization.orgPlanSelection;
    const orgSubscriptionStatus = organization.subscriptionStatus;
    
    // Explicitly hide for paid plans (startup, growth, premium)
    // This applies to ALL users in the organization (org_admin and org_user)
    const paidPlans = ['startup', 'growth', 'premium'];
    if (paidPlans.includes(orgPlan)) {
      return null;
    }
    
    // Explicitly hide if organization subscription is active (paid subscription)
    // Active subscription means organization has paid plan, no trial banner needed
    if (orgSubscriptionStatus === 'active') {
      return null;
    }
    
    // Only show banner if:
    // 1. Organization plan is 'free' (not paid)
    // 2. Organization subscription status is 'trialing' (not active)
    // 3. Trial end date exists
    // This ensures same behavior for org_admin and org_user
    if (orgPlan !== 'free' || 
        orgSubscriptionStatus !== 'trialing' || 
        !organization.trialEnd) {
      return null;
    }
    
    const now = currentTime; // Use currentTime state to trigger updates
    const trialEnd = new Date(organization.trialEnd);
    const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    
    // Return null if expired or invalid
    if (trialEnd < now || daysRemaining < 0) {
      return null;
    }
    
    return daysRemaining;
  }, [organization, currentTime]);

  // Fetch team members data
  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/users/team-members');
      if (response.data.success) {
        const members = response.data.data;
        setTeamMembers(members);

        // Calculate team stats
        const directReports = members.filter(member =>
          member.reportingManager && (member.reportingManager._id === user?.id || member.reportingManager.id === user?.id)
        ).length;
        const indirectReports = members.filter(member =>
          member.reportingManager &&
          member.reportingManager._id !== user?.id &&
          member.reportingManager.id !== user?.id
        ).length;
        const totalMembers = members.length; // All team members including current user

        setTeamStats({
          totalMembers,
          directReports,
          indirectReports,
          activeMembers: 0 // Will be updated by fetchTeamAnalytics
        });

        console.log('✅ Team members fetched:', {
          membersCount: members.length,
          stats: { totalMembers, directReports, indirectReports },
          members: members.map(m => ({
            name: `${m.firstName} ${m.lastName}`,
            id: m._id,
            email: m.email,
            reportingTo: m.reportingTo,
            allFields: Object.keys(m)
          }))
        });
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      // Fallback to hardcoded data if API fails
      setTeamMembers([
        { _id: '68b2c559f43d78cbf207113d', firstName: 'jaimin', lastName: 'Dholakia', reportingTo: null },
        { _id: '68b2c746f43d78cbf20711a4', firstName: 'J5 user', lastName: 'User', reportingTo: '68b2c559f43d78cbf207113d' },
        { _id: '68b4767871abba8df30c4198', firstName: 'Mahima', lastName: 'Sharma', reportingTo: '68b2c559f43d78cbf207113d' },
        { _id: '68b4b71fcae0b16f4808e879', firstName: 'Deep', lastName: 'Python', reportingTo: '68b2c4767871abba8df30c4198' },
        { _id: '68b4c8b5cae0b16f48099e37', firstName: 'Divyesh', lastName: 'PM', reportingTo: '68b2c559f43d78cbf207113d' },
        { _id: '68b561a43e7dcb4a367dd153', firstName: 'Dhara', lastName: 'Pm', reportingTo: '68b2c559f43d78cbf207113d' },
        { _id: '68b6907fcbe4bdf39a6b509f', firstName: 'jenish', lastName: 'Dholariya', reportingTo: '68b4767871abba8df30c4198' }
      ]);

      // Calculate stats from fallback data
      const fallbackMembers = [
        { _id: '68b2c559f43d78cbf207113d', firstName: 'jaimin', lastName: 'Dholakia', reportingTo: null },
        { _id: '68b2c746f43d78cbf20711a4', firstName: 'J5 user', lastName: 'User', reportingTo: '68b2c559f43d78cbf207113d' },
        { _id: '68b4767871abba8df30c4198', firstName: 'Mahima', lastName: 'Sharma', reportingTo: '68b2c559f43d78cbf207113d' },
        { _id: '68b4b71fcae0b16f4808e879', firstName: 'Deep', lastName: 'Python', reportingTo: '68b4767871abba8df30c4198' },
        { _id: '68b4c8b5cae0b16f48099e37', firstName: 'Divyesh', lastName: 'PM', reportingTo: '68b2c559f43d78cbf207113d' },
        { _id: '68b561a43e7dcb4a367dd153', firstName: 'Dhara', lastName: 'Pm', reportingTo: '68b2c559f43d78cbf207113d' },
        { _id: '68b6907fcbe4bdf39a6b509f', firstName: 'jenish', lastName: 'Dholariya', reportingTo: '68b4767871abba8df30c4198' }
      ];

      const directReports = fallbackMembers.filter(member =>
        member.reportingTo === user?.id
      ).length;
      const indirectReports = fallbackMembers.filter(member =>
        member.reportingTo !== user?.id && member.reportingTo
      ).length;
      const totalMembers = fallbackMembers.length;

      setTeamStats({
        totalMembers,
        directReports,
        indirectReports,
        activeMembers: 0 // Will be updated by fetchTeamAnalytics
      });

      console.log('⚠️ Using fallback team data:', {
        totalMembers,
        directReports,
        indirectReports,
        fallbackMembersCount: fallbackMembers.length
      });
    }
  }, [user?.id]);

  // Expired overlay with backdrop blur & CTA to renew (full-page, fixed, disables scroll)
  // Plan selection state (used by overlay)
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [upgradeMemberCount, setUpgradeMemberCount] = useState({}); // Store member count per plan: { 'standard': 1, 'premium': 1 }
  
  // Verify coupon code function
  const verifyCouponCode = async (code) => {
    if (!code || code.trim() === '') {
      setActualCouponId(null);
      setCouponValid(null);
      setCouponDiscount(null);
      return;
    }

    setCouponVerifying(true);
    try {
      // Get current member count (total slots = admin + members)
      let totalSeats = undefined;
      if (organization) {
        const isFree = organization?.orgPlanSelection === 'free';
        const now = new Date();
        const userTrialEnd = organization?.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
        const freeTrialExpired = isFree && userTrialEnd && now > userTrialEnd;
        
        let currentSlots;
        if (isFree && freeTrialExpired) {
          currentSlots = organization?.usedMemberSlots || 5;
        } else {
          currentSlots = organization?.paidMemberSlots || organization?.usedMemberSlots || 5;
        }
        
        // Use selected plan's member count if available
        if (selectedPlanForRenewal && upgradeMemberCount[selectedPlanForRenewal]) {
          totalSeats = upgradeMemberCount[selectedPlanForRenewal] + 1;
        } else {
          totalSeats = currentSlots;
        }
      }
      
      const isRazorpay = organization?.paymentGateway === 'razorpay';
      const verifyUrl = isRazorpay
        ? '/api/v1/public/razorpay-billing/verify-coupon'
        : '/api/v1/public/billing/verify-coupon';

      const response = await api.post(verifyUrl, {
        couponCode: code.trim(),
        memberCount: totalSeats,
        organizationId: organization?._id,
      });

      if (response.data.success && response.data.data.valid) {
        setCouponValid(true);
        setCouponDiscount(response.data.data.discount);
        setActualCouponId(response.data.data.couponCode);
        toast.success(`Coupon applied! ${response.data.data.discount}`);
      } else {
        setCouponValid(false);
        setCouponDiscount(null);
        setActualCouponId(null);
        toast.error(response.data.message || 'Invalid coupon code');
      }
    } catch (error) {
      setCouponValid(false);
      setCouponDiscount(null);
      setActualCouponId(null);
      toast.error(error.response?.data?.message || 'Failed to verify coupon');
    } finally {
      setCouponVerifying(false);
    }
  };

  const overlay = restricted && orgLoaded && typeof document !== 'undefined'
    ? createPortal(
        <Modal
          open={true}
          onCancel={() => {}}
          footer={null}
          closable={false}
          width={700}
          centered
          className="subscription-expiry-modal"
          styles={{
            mask: { backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)' },
            content: { backgroundColor: 'transparent', boxShadow: 'none', padding: 0 },
            body: { padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', maxHeight: '90vh', overflowY: 'auto' }
          }}
        >
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-gradient-to-br from-zinc-900/98 via-zinc-800/98 to-zinc-900/98 border border-white/10 rounded-3xl p-6 backdrop-blur-xl shadow-2xl w-full max-w-[700px] mx-auto max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset' }}
            >
              {/* Icon */}
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  rotate: [0, 3, -3, 0]
                }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                className="w-20 h-20 bg-gradient-to-br from-amber-500/25 to-orange-500/25 rounded-full flex items-center justify-center border-2 border-amber-400/40 mx-auto mb-6"
                style={{ boxShadow: '0 0 30px rgba(251, 191, 36, 0.2)' }}
              >
                <AlertTriangle className="w-10 h-10 text-amber-400" strokeWidth={2.5} />
              </motion.div>

              {/* Title */}
              <Title level={2} className="!mb-3 !text-white !text-3xl !font-bold !leading-tight text-center">
                Plan Has Expired
              </Title>
              <Paragraph className="!text-gray-400 !text-base !mb-0 !leading-relaxed max-w-md mx-auto text-center">
                Renew your plan to regain full access to all features
              </Paragraph>

            {/* Content */}
            {!showPlanModal ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="space-y-6"
              >
                {/* Info Card */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Card
                    className="border-orange-500/25 bg-gradient-to-br from-orange-50/10 via-amber-50/8 to-orange-50/10 backdrop-blur-sm"
                    style={{
                      borderRadius: '16px',
                      border: '1px solid rgba(251, 146, 60, 0.25)',
                      boxShadow: '0 4px 16px rgba(251, 146, 60, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
                      background: 'linear-gradient(135deg, rgba(255, 237, 213, 0.08) 0%, rgba(255, 247, 237, 0.06) 50%, rgba(255, 237, 213, 0.08) 100%)'
                    }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400/20 to-amber-400/15 rounded-full flex items-center justify-center flex-shrink-0 border border-orange-400/30 shadow-sm">
                        <Info className="w-6 h-6 text-orange-400" strokeWidth={2.5} />
                      </div>
                      <div className="flex-1 pt-1">
                        <Text className="text-orange-300 font-bold block mb-2.5 text-base tracking-tight">
                          Subscription Expired
                        </Text>
                        <Text className="text-gray-300 text-sm leading-relaxed font-normal">
                          Your subscription period has ended. To continue using Team Grid with full access to all features, please renew your plan.
                        </Text>
                      </div>
                    </div>
                  </Card>
                </motion.div>

                {/* Renew Now Button */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="primary"
                    size="large"
                    block
                    onClick={() => setShowPlanModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 border-blue-600"
                    style={{ borderRadius: '12px', height: '52px', fontSize: '16px', fontWeight: '600' }}
                    icon={<ArrowRight className="w-5 h-5" />}
                  >
                    Renew Now
                  </Button>
                </motion.div>

                {/* Logout Button */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    type="default"
                    size="large"
                    block
                    onClick={logout}
                    className="bg-white/5 hover:bg-white/10 border-white/20 text-white"
                    style={{ borderRadius: '12px', height: '48px', fontSize: '15px', fontWeight: '500' }}
                    icon={<LogOut className="w-4 h-4" />}
                  >
                    Logout
                  </Button>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="space-y-4"
              >
                {/* Close Button */}
                <div className="flex justify-end">
                  <Button
                    type="text"
                    icon={<X className="w-5 h-5" />}
                    onClick={() => {
                      setShowPlanModal(false);
                      setUpgradeMemberCount({});
                      setSelectedPlanForRenewal(null);
                      setCouponCode('');
                      setActualCouponId(null);
                      setCouponValid(null);
                      setCouponDiscount(null);
                    }}
                    className="text-white/60 hover:text-white hover:bg-white/10"
                    style={{ borderRadius: '8px' }}
                  />
                </div>

                  {/* Plan Selection Header */}
                  <div className="text-center mb-3">
                    <Title level={4} className="!text-white !mb-1 !text-lg !font-bold">
                      Select a Plan to Renew
                    </Title>
                    <Paragraph className="!text-gray-400 !text-xs !mb-0">
                      Choose a plan that best fits your team's needs
                    </Paragraph>
                  </div>

                  {/* Plan Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(() => {
                      if (!organization?.orgPlanSelection) return [];
                      const now = new Date();
                      const isFree = organization.orgPlanSelection === 'free';
                      
                      // Check user-level expiry
                      const userStatus = organization.userSubscriptionStatus;
                      const userEndDate = organization.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
                      const userExpired = !isFree && (
                        ['past_due','canceled','incomplete_expired','unpaid','expired'].includes(userStatus) ||
                        (userEndDate && now > userEndDate)
                      );
                      
                      // Check organization-level expiry
                      const orgStatus = organization.subscriptionStatus;
                      const orgPeriodEnd = organization.currentPeriodEnd ? new Date(organization.currentPeriodEnd) : null;
                      const orgExpired = !isFree && (
                        ['past_due','canceled','incomplete_expired','unpaid','expired'].includes(orgStatus) ||
                        (orgPeriodEnd && now > orgPeriodEnd)
                      );
                      
                      const isExpired = userExpired || orgExpired;
                      
                      if (isExpired) {
                        return ['standard','premium'];
                      }
                      switch (organization.orgPlanSelection) {
                        case 'free':
                          return ['standard','premium'];
                        case 'standard':
                          return ['premium'];
                        case 'premium':
                          return ['standard'];
                        default:
                          return [];
                      }
                    })().map((plan, index) => (
                      <motion.div
                        key={plan}
                        initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        whileHover={{ scale: 1.02, y: -4 }}
                      >
                        <Card
                          className={`border-2 transition-all cursor-pointer h-full ${
                            selectedPlanForRenewal === plan
                              ? plan === 'premium'
                                ? 'border-purple-400 bg-purple-500/15'
                                : plan === 'standard'
                                ? 'border-blue-400 bg-blue-500/15'
                                : 'border-white/40 bg-white/10'
                              : plan === 'premium'
                              ? 'border-purple-500/40 bg-purple-500/10 hover:border-purple-400/60 hover:bg-purple-500/15'
                              : plan === 'standard'
                              ? 'border-blue-500/40 bg-blue-500/10 hover:border-blue-400/60 hover:bg-blue-500/15'
                              : 'border-white/20 bg-white/5 hover:bg-white/10'
                          }`}
                          style={{ borderRadius: '16px' }}
                          onClick={() => setSelectedPlanForRenewal(plan)}
                        >
                          <div className="text-center">
                            <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                              plan === 'premium' ? 'bg-purple-500/20' : plan === 'standard' ? 'bg-blue-500/20' : 'bg-white/10'
                            }`}>
                              {plan === 'premium' ? (
                                <Crown className={`w-5 h-5 ${plan === 'premium' ? 'text-purple-400' : 'text-white'}`} />
                              ) : plan === 'standard' ? (
                                <Zap className="w-5 h-5 text-blue-400" />
                              ) : (
                                <Sparkles className="w-5 h-5 text-white" />
                              )}
                            </div>
                            <Title level={4} className={`!mb-0.5 !text-base !font-bold ${
                              plan === 'premium' ? '!text-purple-300' : plan === 'standard' ? '!text-blue-300' : '!text-white'
                            }`}>
                              {plan.charAt(0).toUpperCase() + plan.slice(1)}
                            </Title>
                            <div className="mb-2">
                              <span className="text-xl font-bold text-white">
                                {formatPrice(plan)}
                          </span>
                              <span className="text-gray-400 text-xs ml-1">/month</span>
                        </div>
                            <Space direction="vertical" size="small" className="w-full mb-2 text-left">
                              {plan === 'premium' ? (
                                <>
                                  <div className="flex items-center text-gray-300 text-sm">
                                    <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                                    <span>All features</span>
                        </div>
                                  <div className="flex items-center text-gray-300 text-sm">
                                    <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                                    <span>Org Intelligence</span>
                        </div>
                                  <div className="flex items-center text-gray-300 text-sm">
                                    <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                                    <span>Priority support</span>
                  </div>
                                </>
                              ) : plan === 'standard' ? (
                                <>
                                  <div className="flex items-center text-gray-300 text-sm">
                                    <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                                    <span>All features</span>
                </div>
                                  <div className="flex items-center text-gray-300 text-sm">
                                    <Check className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" />
                                    <span>Unlimited members</span>
                                  </div>
                                  <div className="flex items-center text-gray-400 text-sm">
                                    <span className="w-4 h-4 mr-2 flex-shrink-0 text-center">✗</span>
                                    <span>No Org Intelligence</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="flex items-center text-gray-300 text-sm">
                                    <Check className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" />
                                    <span>1 month full AI access</span>
                                  </div>
                                  {/* <div className="flex items-center text-gray-300 text-sm">
                                    <Check className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" />
                                    <span>Basic features after trial</span>
                                  </div> */}
                                </>
                              )}
                            </Space>
                            
                            {/* Member Count Input Section - Only for Standard and Premium */}
                            {(plan === 'standard' || plan === 'premium') && (
                              <div className={`mb-2 p-2 rounded-lg border ${
                                plan === 'premium'
                                  ? 'bg-white/5 border-white/10'
                                  : 'bg-white/5 border-white/10'
                              }`}>
                                <div className="mb-1.5">
                                  <Text className="text-xs block mb-1 font-medium text-gray-300">
                                    Number of Team Members
                                  </Text>
                                  {(() => {
                                    // Calculate minimum member count (current used - 1 for admin)
                                    const isFree = organization?.orgPlanSelection === 'free';
                                    const now = new Date();
                                    const userTrialEnd = organization?.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
                                    const freeTrialExpired = isFree && userTrialEnd && now > userTrialEnd;
                                    
                                    let currentSlots;
                                    if (isFree && freeTrialExpired) {
                                      currentSlots = organization?.usedMemberSlots || 5;
                                    } else {
                                      currentSlots = organization?.paidMemberSlots || organization?.usedMemberSlots || 5;
                                    }
                                    
                                    const minMemberCount = Math.max(1, currentSlots - 1);
                                    const memberCount = upgradeMemberCount[plan] || minMemberCount;
                                    const totalSeats = memberCount + 1; // +1 for admin
                                    const planAmount = getPrice(plan);
                                    const monthlyPrice = totalSeats * planAmount;
                                    
                                    return (
                                      <>
                                        <InputNumber
                                          min={minMemberCount}
                                          max={1000}
                                          value={memberCount}
                                          onChange={(value) => {
                                            setUpgradeMemberCount(prev => ({
                                              ...prev,
                                              [plan]: value || minMemberCount
                                            }));
                                          }}
                                          controls={true}
                                          className="w-full"
                                          style={{ width: '100%' }}
                                          size="small"
                                        />
                                        <Text className="text-xs block mt-1 text-gray-400">
                                          Currently using: {currentSlots} slots (1 admin + {currentSlots - 1} members)
                                        </Text>
                                        <Text className="text-xs block mt-0.5 text-gray-400">
                                          Total seats: {totalSeats} (1 admin + {memberCount} members)
                                        </Text>
                                        <Divider className="!my-1.5 !border-gray-300/50" />
                                        <div className="text-center">
                                          <Text className="text-xs block text-gray-400">
                                            Monthly Total
                                          </Text>
                                          <Text className={`text-base font-bold block ${
                                            plan === 'premium' ? 'text-purple-300' : 'text-blue-300'
                                          }`}>
                                            {symbol}{monthlyPrice.toFixed(2)}/month
                                          </Text>
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                            
                            <Button
                              type={plan === 'premium' || plan === 'standard' ? 'primary' : 'default'}
                              size="large"
                              block
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlanSelection(plan);
                              }}
                              disabled={processingPlan !== null}
                              loading={processingPlan === plan}
                              className={
                                plan === 'premium'
                                  ? 'bg-purple-600 hover:bg-purple-700 border-purple-600'
                                  : plan === 'standard'
                                  ? 'bg-blue-600 hover:bg-blue-700 border-blue-600'
                                  : 'bg-white/10 hover:bg-white/20 border-white/20'
                              }
                              style={{ borderRadius: '8px', height: '40px', fontSize: '14px', fontWeight: '600' }}
                              icon={processingPlan === plan ? null : <CreditCard className="w-4 h-4" />}
                            >
                              {processingPlan === plan ? 'Processing...' : selectedPlanForRenewal === plan ? 'Proceed to Payment' : 'Select Plan'}
                            </Button>
            </div>
                        </Card>
                      </motion.div>
                    ))}
          </div>
                  
                  {/* Coupon Code Section - Always visible */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-3 p-3 rounded-lg border bg-white/5 border-white/10"
                  >
                      <label className="block text-sm font-medium mb-2 text-gray-300">
                        Have a coupon or promotion code? (Optional)
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={couponCode}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              setCouponCode(value);
                              
                              // Clear previous timeout
                              if (couponTimeoutRef.current) {
                                clearTimeout(couponTimeoutRef.current);
                              }
                              
                              // Verify after user stops typing (debounce)
                              if (value.trim() === '') {
                                setActualCouponId(null);
                                setCouponValid(null);
                                setCouponDiscount(null);
                              } else {
                                // Debounce verification - wait 800ms after user stops typing
                                couponTimeoutRef.current = setTimeout(() => {
                                  verifyCouponCode(value);
                                }, 800);
                              }
                            }}
                            onBlur={() => {
                              if (couponCode.trim() !== '') {
                                verifyCouponCode(couponCode);
                              }
                            }}
                            placeholder="Enter coupon code"
                            className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                            disabled={couponVerifying}
                          />
                          {couponVerifying && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            </div>
                          )}
                        </div>
                      </div>
                      {couponValid === true && couponDiscount && (
                        <Text className="text-xs mt-1.5 block font-medium text-green-400">
                          ✅ Valid! {couponDiscount} will be applied at checkout
                        </Text>
                      )}
                      {couponValid === false && (
                        <Text className="text-xs mt-1.5 block text-red-400">
                          ❌ Invalid coupon code. Please check and try again.
                        </Text>
                      )}
                    </motion.div>
              </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </Modal>,
        document.body
      )
    : null;

  // Disable page scroll when overlay active
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const original = document.body.style.overflow;
    if (restricted && orgLoaded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = original || '';
    }
    return () => {
      document.body.style.overflow = original || '';
    };
  }, [restricted, orgLoaded]);

  // Plan selection handlers (for Renew flow)
  // If organization subscription expired, use organization-level checkout
  const handlePlanSelection = async (plan) => {
    setProcessingPlan(plan);
    try {
      const now = new Date();
      const isFree = organization?.orgPlanSelection === 'free';
      const orgStatus = organization?.subscriptionStatus;
      const orgPeriodEnd = organization?.currentPeriodEnd ? new Date(organization.currentPeriodEnd) : null;
      const orgExpired = !isFree && (
        ['past_due', 'canceled', 'incomplete_expired', 'unpaid', 'expired'].includes(orgStatus) ||
        (orgPeriodEnd && now > orgPeriodEnd)
      );

      // If organization subscription expired OR free plan trial expired, use organization-level checkout
      const userTrialEnd = organization?.userSubscriptionEndDate ? new Date(organization.userSubscriptionEndDate) : null;
      const freeTrialExpired = isFree && userTrialEnd && now > userTrialEnd;

      if (orgExpired || freeTrialExpired) {
        // Only org_admin can renew organization subscription
        if (user?.role === 'org_admin' || user?.role === 'main_admin') {
          // Use organization-level subscription checkout for renewal
          // For free plan renewal: pay for ALL current members (including 5 free ones)
          // For paid plan renewal: maintain same number of paid slots
          let currentSlots, memberCount;

          if (isFree && freeTrialExpired) {
            // Free plan renewal: pay for all current members (usedMemberSlots)
            currentSlots = organization?.usedMemberSlots || 1; // All current members including admin
            // Ensure minimum 1 member (if only admin exists, still need 1 member slot)
            const minMemberCount = Math.max(1, currentSlots - 1); // Minimum 1 member
            // Get member count for this plan (default to minimum if not set)
            memberCount = upgradeMemberCount[plan] || minMemberCount;
          } else {
            // Paid plan renewal: maintain same paid slots
            currentSlots = organization?.paidMemberSlots || 1;
            // Ensure minimum 1 member (if only admin exists, still need 1 member slot)
            const minMemberCount = Math.max(1, currentSlots - 1); // Minimum 1 member
            // Get member count for this plan (default to minimum if not set)
            memberCount = upgradeMemberCount[plan] || minMemberCount;
          }

          // Calculate total amount for user confirmation
          const planAmount = getPrice(plan);
          const totalSeats = memberCount + 1; // +1 for admin (use selected memberCount)
          const totalAmount = totalSeats * planAmount;

          console.log('🔄 Renewal Checkout Calculation:', {
            isFreePlanRenewal: isFree && freeTrialExpired,
            currentPaidSlots: organization?.paidMemberSlots,
            currentUsedSlots: organization?.usedMemberSlots,
            currentSlots: currentSlots,
            breakdown: `${currentSlots} total slots = 1 admin + ${memberCount} members`,
            memberCount: memberCount,
            totalSeats: totalSeats,
            plan: plan,
            planAmount: planAmount,
            totalAmount: totalAmount,
            calculation: `${totalSeats} seats × ${symbol}${planAmount} = ${symbol}${totalAmount}/month`,
            note: isFree && freeTrialExpired
              ? 'Free plan renewal: Paying for all current members (including 5 free ones)'
              : 'This includes admin + all existing members'
          });

          toast.loading(`Creating checkout for ${totalSeats} seats (${memberCount} members + 1 admin)...`, { id: 'renewal-loading' });

          // Try Razorpay first (for Indian users)
          let rzpRes;
          try {
            rzpRes = await api.post('/api/v1/public/razorpay-billing/organization-subscription-checkout', {
              userId: user?.id,
              plan: plan,
              memberCount: memberCount,
              ...(actualCouponId && { couponCode: actualCouponId })
            });
          } catch { rzpRes = null; }

          if (rzpRes?.data?.success && (rzpRes?.data?.data?.subscriptionId || rzpRes?.data?.data?.orderId)) {
            toast.dismiss('renewal-loading');
            const rzpData = rzpRes.data.data;
            openRazorpayCheckout({
              razorpayKeyId: rzpData.razorpayKeyId,
              amount: rzpData.amount,
              currency: rzpData.currency,
              orderId: rzpData.orderId,
              subscriptionId: rzpData.subscriptionId,
              customerName: rzpData.customerName,
              customerEmail: rzpData.customerEmail,
              notes: rzpData.notes,
              description: `TeamGrid ${plan} Plan Renewal`,
              onSuccess: async (paymentResponse) => {
                try {
                  const verifyRes = await api.post('/api/v1/public/razorpay-billing/verify-payment', {
                    razorpay_order_id: paymentResponse.razorpay_order_id,
                    razorpay_payment_id: paymentResponse.razorpay_payment_id,
                    razorpay_signature: paymentResponse.razorpay_signature,
                    razorpay_subscription_id: paymentResponse.razorpay_subscription_id,
                    userId: user?.id,
                    organizationId: rzpData.notes.organizationId,
                    plan, memberCount, totalSeats: rzpData.totalSeats,
                    type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'organization_subscription',
                  });
                  if (verifyRes.data.success) {
                    if (verifyRes.data.data.token) {
                      localStorage.setItem('token', verifyRes.data.data.token);
                      localStorage.setItem('refreshToken', verifyRes.data.data.refreshToken);
                      api.updateToken(verifyRes.data.data.token);
                    }
                    toast.success('Payment successful!');
                    setTimeout(() => { window.location.href = '/dashboard?payment=success'; }, 100);
                  } else {
                    toast.error(verifyRes.data.message || 'Payment verification failed');
                    setProcessingPlan(null);
                  }
                } catch { toast.error('Payment verification failed'); setProcessingPlan(null); }
              },
              onFailure: (error) => { toast.error(error?.description || 'Payment failed'); setProcessingPlan(null); },
              onDismiss: () => { setProcessingPlan(null); },
            });
            return;
          }

          // Stripe flow (international users)
          const response = await api.post('/api/v1/public/billing/organization-subscription-checkout', {
            userId: user?.id,
            plan: plan,
            memberCount: memberCount,
            ...(actualCouponId && { couponCode: actualCouponId })
          });

          if (response.data?.success) {
            toast.dismiss('renewal-loading');
            const url = response.data?.data?.url;
            if (url) {
              window.location.href = url;
            } else {
              toast.error('Checkout URL not received');
              setProcessingPlan(null);
            }
          } else {
            toast.dismiss('renewal-loading');
            toast.error(response.data?.message || 'Failed to start checkout');
            setProcessingPlan(null);
          }
          return;
        } else {
          // Regular members can't renew organization subscription
          toast.error('Organization subscription has expired. Please contact your administrator to renew.');
          setProcessingPlan(null);
          return;
        }
      }

      // User-level subscription checkout (for individual user subscription)
      if (plan === 'free') {
        const response = await api.post('/api/v1/public/billing/user-subscription-checkout', {
          userId: user?.id, plan,
          ...(couponCode && { couponCode: couponCode.trim() })
        });
        if (response.data?.success) {
          const token = response.data?.data?.token;
          const refreshToken = response.data?.data?.refreshToken;
          if (token && refreshToken) {
            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', refreshToken);
          }
          window.location.href = '/dashboard';
        } else {
          toast.error(response.data?.message || 'Failed to start checkout');
        }
      } else {
        // Paid user plan - try Razorpay first
        let rzpUserRes;
        try {
          rzpUserRes = await api.post('/api/v1/public/razorpay-billing/user-subscription-checkout', {
            userId: user?.id, plan,
            ...(couponCode && { couponCode: couponCode.trim() })
          });
        } catch { rzpUserRes = null; }

        if (rzpUserRes?.data?.success && (rzpUserRes?.data?.data?.subscriptionId || rzpUserRes?.data?.data?.orderId)) {
          const rzpData = rzpUserRes.data.data;
          openRazorpayCheckout({
            razorpayKeyId: rzpData.razorpayKeyId,
            subscriptionId: rzpData.subscriptionId,
            orderId: rzpData.orderId,
            amount: rzpData.amount,
            currency: rzpData.currency,
            customerName: rzpData.customerName,
            customerEmail: rzpData.customerEmail,
            notes: rzpData.notes,
            description: `TeamGrid ${plan} Plan`,
            onSuccess: async (paymentResponse) => {
              try {
                const verifyRes = await api.post('/api/v1/public/razorpay-billing/verify-payment', {
                  razorpay_order_id: paymentResponse.razorpay_order_id,
                  razorpay_subscription_id: paymentResponse.razorpay_subscription_id,
                  razorpay_payment_id: paymentResponse.razorpay_payment_id,
                  razorpay_signature: paymentResponse.razorpay_signature,
                  userId: user?.id,
                  organizationId: rzpData.notes.organizationId,
                  plan, totalSeats: '1', type: rzpData.billingType === 'one_time' ? 'one_time_payment' : 'user_subscription',
                });
                if (verifyRes.data.success) {
                  if (verifyRes.data.data.token) {
                    localStorage.setItem('token', verifyRes.data.data.token);
                    localStorage.setItem('refreshToken', verifyRes.data.data.refreshToken);
                  }
                  toast.success('Payment successful!');
                  setTimeout(() => { window.location.href = '/dashboard?payment=success'; }, 100);
                } else { toast.error('Payment verification failed'); }
              } catch { toast.error('Payment verification failed'); }
            },
            onFailure: (error) => { toast.error(error?.description || 'Payment failed'); },
            onDismiss: () => { setProcessingPlan(null); },
          });
        } else {
          // Stripe flow
          const response = await api.post('/api/v1/public/billing/user-subscription-checkout', {
            userId: user?.id, plan,
            ...(couponCode && { couponCode: couponCode.trim() })
          });
          if (response.data?.success) {
            const url = response.data?.data?.url;
            if (url) { window.location.href = url; }
            else { toast.error('Checkout URL not received'); }
          } else { toast.error(response.data?.message || 'Failed to start checkout'); }
        }
      }
    } catch (error) {
      console.error('Plan selection error:', error);
      toast.error(error.response?.data?.message || 'Failed to process plan selection');
    } finally {
      setProcessingPlan(null);
    }
  };

  // Fetch productivity data from hourly summary
  const fetchProductivityData = useCallback(async () => {
    try {
      // Build query params for the date range
      const params = new URLSearchParams();
      if (startDate) {
        const startDateStr = formatDateForAPI(startDate, false);
        params.append('startDate', startDateStr ? startDateStr.split('T')[0] : '');
      }
      if (endDate) {
        const endDateStr = formatDateForAPI(endDate, true);
        params.append('endDate', endDateStr ? endDateStr.split('T')[0] : '');
      }

      let endpoint = '/api/v1/hourly-summaries';
      if (selectedTeamMember) {
        params.append('userId', selectedTeamMember);
      }

      const response = await api.get(`${endpoint}?${params.toString()}`);

      if (response.data.success) {
        const summaries = response.data.data.summaries || [];

        if (summaries.length > 0) {
          // Calculate productivity scores from hourly summaries
          const productivityScores = summaries.map(summary => {
            // Assuming productivity is based on focus time vs total time
            const totalTime = summary.totalTime || 0;
            const focusTime = summary.focusTime || 0;
            const productivity = totalTime > 0 ? Math.round((focusTime / totalTime) * 100) : 0;
            return Math.min(productivity, 100); // Cap at 100%
          });

          const myScore = productivityScores[0] || 0; // First entry is usually the selected user
          const teamAverage = productivityScores.length > 1
            ? Math.round(productivityScores.reduce((sum, score) => sum + score, 0) / productivityScores.length)
            : myScore;
          const overallScore = Math.round((myScore + teamAverage) / 2);

          setProductivityData({
            myScore,
            teamAverage,
            overallScore
          });

          console.log('✅ Productivity data calculated from hourly summaries:', {
            summaries: summaries.length,
            productivityScores,
            myScore,
            teamAverage,
            overallScore
          });
        } else {
          // No data available, use fallback
          setProductivityData({
            myScore: 85,
            teamAverage: 82,
            overallScore: 84
          });
        }
      }
    } catch (error) {
      console.error('Error fetching productivity data from hourly summaries:', error);
      // Fallback data
      setProductivityData({
        myScore: 85,
        teamAverage: 82,
        overallScore: 84
      });
    }
  }, [startDate, endDate, selectedTeamMember]);

  // Fetch team average score from DailySummary table
  const fetchTeamAverageScore = useCallback(async () => {
    console.log('🚀 fetchTeamAverageScore function called');
    console.log('🔍 Selected team member:', selectedTeamMember);
    try {
      // Use the dashboard's selected date range (should now default to 2025-09-03)
      const startDateStr = formatDateForAPI(startDate, false)?.split('T')[0] || '';
      const endDateStr = formatDateForAPI(endDate, true)?.split('T')[0] || '';

      console.log('📅 Dashboard date range:', { startDateStr, endDateStr });

      console.log('🔍 Fetching team average for date range:', startDateStr, 'to', endDateStr);

      // If a specific team member is selected, get their individual score
      if (selectedTeamMember) {
        console.log('👤 Fetching individual score for selected member:', selectedTeamMember);
        const response = await api.get(`/api/v1/daily-summaries/${startDateStr}`, {
          params: { userId: selectedTeamMember }
        });

        if (response.data.success && response.data.data) {
          const memberScore = response.data.data.aiAnalysis?.avgProductivityScore || 0;
          console.log('👤 Member individual score:', memberScore);
          setTeamAverageData({
            teamAverage: memberScore,
            userScore: memberScore,
            teamMembersCount: 1,
            validScoresCount: memberScore > 0 ? 1 : 0,
            isIndividualScore: true
          });
          return;
        } else {
          console.log('👤 No data found for selected member');
          setTeamAverageData({
            teamAverage: 0,
            userScore: 0,
            teamMembersCount: 1,
            validScoresCount: 0,
            isIndividualScore: true
          });
          return;
        }
      }

      // If date range is more than 1 day, calculate average across multiple days
      if (startDateStr === endDateStr) {
        // Single day - use existing endpoint
        console.log('📅 Single day calculation for:', startDateStr);
        const response = await api.get(`/api/v1/daily-summaries/team-average/${startDateStr}`);

        if (response.data.success) {
          setTeamAverageData(response.data.data);
          console.log('✅ Team average data fetched:', response.data.data);
        } else {
          console.warn('❌ Team average API returned unsuccessful response:', response.data);
        }
      } else {
        // Date range - calculate average across multiple days
        console.log('📅 Date range calculation from', startDateStr, 'to', endDateStr);

        const datePromises = [];
        const currentDate = new Date(startDateStr);
        const endDateObj = new Date(endDateStr);

        // Create promises for each day in the range
        while (currentDate <= endDateObj) {
          const dateStr = currentDate.toISOString().split('T')[0];
          datePromises.push(
            api.get(`/api/v1/daily-summaries/team-average/${dateStr}`)
              .then(res => res.data.success ? res.data.data : null)
              .catch(() => null)
          );
          currentDate.setDate(currentDate.getDate() + 1);
        }

        const dailyResults = await Promise.all(datePromises);
        const validResults = dailyResults.filter(result => result && result.teamAverage > 0);

        if (validResults.length > 0) {
          // Calculate average of daily team averages
          const rangeTeamAverage = Math.round(
            validResults.reduce((sum, day) => sum + day.teamAverage, 0) / validResults.length
          );

          // Calculate average of user scores across the range
          const userScores = validResults.filter(day => day.userScore > 0).map(day => day.userScore);
          const rangeUserAverage = userScores.length > 0
            ? Math.round(userScores.reduce((sum, score) => sum + score, 0) / userScores.length)
            : 0;

          setTeamAverageData({
            teamAverage: rangeTeamAverage,
            userScore: rangeUserAverage,
            teamMembersCount: validResults[0]?.teamMembersCount || 0,
            validScoresCount: validResults.length,
            dateRange: `${startDateStr} to ${endDateStr}`
          });

          console.log('✅ Date range team average calculated:', {
            rangeTeamAverage,
            rangeUserAverage,
            daysWithData: validResults.length
          });
        } else {
          console.warn('❌ No valid team average data found in date range');
          setTeamAverageData({
            teamAverage: 0,
            userScore: 0,
            teamMembersCount: 0,
            validScoresCount: 0
          });
        }
      }
    } catch (error) {
      console.error('❌ Error fetching team average score:', error.response?.data || error.message);
      // Set fallback data
      setTeamAverageData({
        teamAverage: 0,
        userScore: 0,
        teamMembersCount: 0,
        validScoresCount: 0
      });
    }
  }, [startDate, endDate, selectedTeamMember]);

  // Fetch goals team stats
  const fetchGoalsTeamStats = useCallback(async () => {
    try {
      setGoalsTeamStatsLoading(true);
      const response = await getTeamStats();

      if (response.success) {
        setGoalsTeamStats(response.data);
        console.log('✅ Goals team stats fetched:', response.data);
      } else {
        console.warn('⚠️ Goals team stats API returned unsuccessful response:', response);
        setGoalsTeamStats({
          teamAverageScore: null,
          completedGoalsCount: 0,
          totalGoalsCount: 0,
          previousMonthAverageScore: null,
          previousMonthChange: null
        });
      }
    } catch (error) {
      console.error('❌ Error fetching goals team stats:', error);
      setGoalsTeamStats({
        teamAverageScore: null,
        completedGoalsCount: 0,
        totalGoalsCount: 0,
        previousMonthAverageScore: null,
        previousMonthChange: null
      });
    } finally {
      setGoalsTeamStatsLoading(false);
    }
  }, []);

  // Note: Project data is now fetched via fetchProjectOverview function
  // This removes duplicate API calls and ensures consistent project counts

  // Fetch team analytics data - fetch ALL members based on hierarchy, then paginate display
  const fetchTeamAnalytics = useCallback(async (sortOrderOverride = null) => {
    try {
      console.log('🚀 Fetching ALL team members with analytics data (hierarchy-based)...');
      setTeamAnalyticsLoading(true);

      const startDateStr = formatDateForAPI(startDate, false)?.split('T')[0] || '';
      const endDateStr = formatDateForAPI(endDate, true)?.split('T')[0] || '';

      const currentSortOrder = sortOrderOverride || teamSortOrder;
      console.log('📅 Team Analytics Date Range:', {
        startDate: startDateStr,
        endDate: endDateStr,
        page: teamPage,
        limit: itemsPerPage,
        sortOrder: currentSortOrder
      });

      // Use server-side pagination - fetch only the current page
      const params = new URLSearchParams();
      params.append('startDate', startDateStr);
      params.append('endDate', endDateStr);
      params.append('page', teamPage.toString());
      params.append('limit', itemsPerPage.toString()); // Fetch only 5 members per page
      params.append('sortOrder', currentSortOrder); // Add sorting parameter for team performance

      // Add userId parameter if a specific team member is selected
      // This will show that member's hierarchy (member + their subordinates)
      // If not provided, shows logged-in user's hierarchy
      if (selectedTeamMember) {
        params.append('userId', selectedTeamMember);
        console.log('👤 Fetching hierarchy for selected team member:', selectedTeamMember, 'page:', teamPage);
      } else {
        console.log('👥 Fetching hierarchy for logged-in user (All Team Members), page:', teamPage);
      }

      const response = await api.get(`/api/v1/live-analytics/team-analytics-paginated?${params.toString()}`);

      if (!response.data.success) {
        console.error('Failed to fetch team analytics:', response.data.message);
        setTeamAnalytics([]);
        return;
      }

      const { teamAnalytics, pagination, summary } = response.data.data;

      // Transform data to match existing format
      const transformedAnalytics = teamAnalytics.map(member => {
        // Calculate active time from activeMinutes if available
        const activeMinutes = member.activeMinutes || 0;
        const activeHours = Math.floor(activeMinutes / 60);
        const activeMins = activeMinutes % 60;
        const activeTimeFormatted = `${activeHours}h ${activeMins}m`;

        // Calculate idle time from idleMinutes if available
        const idleMinutes = member.idleMinutes || 0;
        const idleHours = Math.floor(idleMinutes / 60);
        const idleMins = idleMinutes % 60;
        const idleTimeFormatted = `${idleHours}h ${idleMins}m`;

        return {
          userId: member.userId,
          firstName: member.firstName || '',
          lastName: member.lastName || '',
          email: member.email || '',
          position: member.position || 'Team Member',
          hours: member.hours || '0h 0m',
          idleTime: idleTimeFormatted,
          activeTime: activeTimeFormatted,
          productivity: member.productivity || 0,
          sessions: member.sessions || 0,
          lastActivity: member.lastActivity || null,
          isCurrentUser: member.isCurrentUser || member.userId === user?.id,
          relationshipLabel: member.isCurrentUser ? 'You' : 'Team Member',
          hasData: member.sessions > 0 || member.totalMinutes > 0,
          badge: member.badge || '',
          badgeIcon: member.badgeIcon || '',
          streak: member.streak || 0,
          streakIcon: member.streakIcon || ''
        };
      });

      // Store paginated members (server-side pagination)
      setTeamAnalytics(transformedAnalytics);

      // Store pagination metadata from API response
      setTeamAnalyticsPagination({
        total: pagination?.total || 0,
        pages: pagination?.pages || 0,
        page: pagination?.page || teamPage
      });

      // Update active members count in teamStats from summary
      setTeamStats(prev => ({
        ...prev,
        activeMembers: summary?.activeMembers || transformedAnalytics.filter(m => m.sessions > 0).length
      }));

      console.log('✅ Team analytics fetched (paginated):', {
        totalMembers: pagination?.total || 0,
        currentPage: pagination?.page || teamPage,
        totalPages: pagination?.pages || 0,
        returned: transformedAnalytics.length,
        activeMembers: summary?.activeMembers || 0,
      });
    } catch (error) {
      console.error('❌ Team analytics failed:', error.message);
      setTeamAnalytics([]);
    } finally {
      setTeamAnalyticsLoading(false);
    }
  }, [startDate, endDate, teamPage, selectedTeamMember, user?.id]); // Include teamPage for server-side pagination

  // Export team analytics to Excel
  const exportTeamAnalyticsToExcel = useCallback(async () => {
    try {
      toast.loading('Preparing export...', { id: 'export-toast' });

      const startDateStr = formatDateForAPI(startDate, false)?.split('T')[0] || '';
      const endDateStr = formatDateForAPI(endDate, true)?.split('T')[0] || '';

      const params = new URLSearchParams();
      params.append('startDate', startDateStr);
      params.append('endDate', endDateStr);
      params.append('sortOrder', teamSortOrder);

      if (selectedTeamMember) {
        params.append('userId', selectedTeamMember);
      }

      const response = await api.get(`/api/v1/live-analytics/team-analytics-export?${params.toString()}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Export failed');
      }

      const { teamAnalytics, exportTime, dateRange } = response.data.data;

      // Prepare Excel data
      const excelData = teamAnalytics.map((member, index) => ({
        'S.No': index + 1,
        'Member Name': `${member.firstName} ${member.lastName}`,
        'Email': member.email,
        'Position': member.position,
        'Active Hours': member.activeTime,
        'Idle Hours': member.idleTime,
        'Total Hours': member.hours
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },   // S.No
        { wch: 25 },  // Member Name
        { wch: 30 },  // Email
        { wch: 20 },  // Position
        { wch: 15 },  // Active Hours
        { wch: 15 },  // Idle Hours
        { wch: 15 }   // Total Hours
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Team Performance');

      // Generate filename with timestamp
      const timestamp = new Date(exportTime).toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `Team_Performance_${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);

      toast.success(`Exported ${teamAnalytics.length} team members to Excel`, { id: 'export-toast' });
    } catch (error) {
      console.error('❌ Export failed:', error);
      toast.error(error.message || 'Failed to export team analytics', { id: 'export-toast' });
    }
  }, [startDate, endDate, teamSortOrder, selectedTeamMember, api]);

  const fetchProjectOverview = useCallback(async (sortOrderOverride = null) => {
    try {
      setProjectOverviewLoading(true);

      const startDateStr = formatDateForAPI(startDate, false)?.split('T')[0] || '';
      const endDateStr = formatDateForAPI(endDate, true)?.split('T')[0] || '';

      const currentSortOrder = sortOrderOverride || projectsSortOrder;
      console.log('📊 Fetching project overview (paginated)...', {
        startDate: startDateStr,
        endDate: endDateStr,
        page: projectsPage,
        limit: itemsPerPage,
        sortOrder: currentSortOrder,
        selectedTeamMember
      });

      // Use new paginated endpoint - single API call instead of multiple
      const params = new URLSearchParams();
      params.append('startDate', startDateStr);
      params.append('endDate', endDateStr);
      params.append('page', projectsPage.toString());
      params.append('limit', itemsPerPage.toString());
      params.append('sortOrder', currentSortOrder); // Add sorting parameter for projects

      // Add userId parameter if a team member is selected
      if (selectedTeamMember) {
        params.append('userId', selectedTeamMember);
      }

      const response = await api.get(`/api/v1/live-analytics/project-overview-paginated?${params.toString()}`);

      if (!response.data.success) {
        console.warn('⚠️ Project overview API failed:', response.data);
        setProjectOverview([]);
        setAllProjects([]);
        return;
      }

      const { projects, pagination, summary } = response.data.data;

      // Transform projects to match existing format
      const transformedProjects = projects.map(project => ({
        _id: project.projectId,
        name: project.name,
        description: project.description,
        status: project.status,
        timeSpent: project.timeSpent || '0h 0m',
        formattedTime: project.timeSpent || '0h 0m',
        avgSession: project.avgSession || '0h 0m',
        avgSessionTime: project.avgSession || '0h 0m',
        averageSession: project.avgSession || '0h 0m',
        memberCount: project.teamMembers || 0, // Assigned active team members count
        isActive: project.isActive !== undefined ? project.isActive : (project.status === 'active'),
        estimatedHours: project.estimatedHours || 0,
        actualHours: project.actualHours || 0
      }));

      // For display, show paginated results from API
      setProjectOverview(transformedProjects);

      // Store paginated results - for modal we'll fetch all when needed
      setAllProjects(transformedProjects);

      // Update project stats from summary
      setProjectStats({
        totalProjects: summary?.totalProjects || 0,
        activeProjects: summary?.activeProjects || 0,
        completedProjects: 0, // Not available in summary, keeping 0 for now
        archivedProjects: 0, // Not available in summary, keeping 0 for now
        totalTimeSpent: summary?.totalTimeSpent || '0h 0m',
        averageSessionTime: '0h 0m', // Not available in summary
        teamMembers: teamMembers.length
      });

      // Update projectsData for key metrics
      const projectsDataUpdate = {
        myProjects: Math.floor((summary?.totalProjects || 0) * 0.4),
        directTeamProjects: Math.floor((summary?.totalProjects || 0) * 0.4),
        indirectTeamProjects: Math.floor((summary?.totalProjects || 0) * 0.2),
        totalProjects: summary?.totalProjects || 0,
        activeProjects: summary?.activeProjects || 0,
        completedProjects: 0,
        archivedProjects: 0
      };

      setProjectsData(projectsDataUpdate);

      // Store pagination metadata
      setProjectOverviewPagination({
        total: pagination?.total || 0,
        pages: pagination?.pages || 0,
        page: pagination?.page || projectsPage
      });

      console.log('✅ Project overview fetched (paginated):', {
        totalProjects: pagination?.total || 0,
        currentPage: pagination?.page || projectsPage,
        totalPages: pagination?.pages || 0,
        returned: transformedProjects.length,
        activeProjects: summary?.activeProjects || 0
      });
    } catch (error) {
      console.error('❌ Project overview API failed:', error.message);
      setProjectOverview([]);
      setAllProjects([]);
    } finally {
      setProjectOverviewLoading(false);
    }
  }, [startDate, endDate, projectsPage, itemsPerPage, selectedTeamMember, teamMembers.length, user?.role, user?.id]); // Keep projectsPage for server-side pagination

  // Member Hours Analysis Functions
  const openMemberHoursPanel = (project) => {
    setSelectedProjectForAnalysis(project);
    setShowMemberHoursModal(true);
    setTimeout(() => setIsMemberHoursPanelAnimating(true), 50);
  };

  const closeMemberHoursPanel = () => {
    setIsMemberHoursPanelAnimating(false);
    setTimeout(() => {
      setShowMemberHoursModal(false);
      setSelectedProjectForAnalysis(null);
    }, 300);
  };

  // Member Analytics Functions
  const openMemberAnalyticsPanel = (member) => {
    setSelectedMemberForAnalytics(member);
    setShowMemberAnalyticsSidebar(true);
    setTimeout(() => setIsMemberAnalyticsPanelAnimating(true), 50);
  };

  const closeMemberAnalyticsPanel = () => {
    setIsMemberAnalyticsPanelAnimating(false);
    setTimeout(() => {
      setShowMemberAnalyticsSidebar(false);
      setSelectedMemberForAnalytics(null);
    }, 300);
  };

  // Fetch most visited data
  const fetchMostVisited = useCallback(async () => {
    try {
      setMostVisitedLoading(true);

      // Build query params for the date range and selected user
      const params = new URLSearchParams();
      if (startDate) {
        const startDateStr = formatDateForAPI(startDate, false);
        params.append('startDate', startDateStr ? startDateStr.split('T')[0] : '');
      }
      if (endDate) {
        const endDateStr = formatDateForAPI(endDate, true);
        params.append('endDate', endDateStr ? endDateStr.split('T')[0] : '');
      }

      // Handle team member selection logic
      if (selectedTeamMember) {
        // Specific team member selected
        params.append('userId', selectedTeamMember);
      } else {
        // "All Team Members" selected - don't send userId parameter
        // Backend will automatically fetch all users in organization for admins
        console.log('📊 All Team Members selected - backend will fetch all organization users');
      }

      // Add server-side pagination parameters
      params.append('page', mostVisitedPage);
      params.append('limit', mostVisitedLimit);

      console.log('🚀 Fetching most visited data for activity tracking');
      console.log('📅 Date range:', {
        startDate: startDate ? formatDateForAPI(startDate, false)?.split('T')[0] : '',
        endDate: endDate ? formatDateForAPI(endDate, true)?.split('T')[0] : '',
        selectedTeamMember,
        isAllTeamMembers: !selectedTeamMember,
        scope: selectedTeamMember ? 'Individual User' : 'All Team Members'
      });

      const response = await api.get(`/api/v1/live-analytics/activity-from-screenshots?${params.toString()}`);

      console.log('📡 Activity from screenshots API response status:', response.status);
      console.log('📡 Activity from screenshots API response data:', response.data);

      if (response.data.success) {
        const mostVisitedData = response.data.data.mostVisited || [];
        setMostVisited(mostVisitedData);
        setMostVisitedDebug(response.data.data);

        // Handle server-side pagination response
        const pagination = response.data.data.summary?.pagination;
        if (pagination) {
          setMostVisitedTotal(pagination.total || 0);
          setMostVisitedHasMore(pagination.hasMore || false);
        } else {
          // Fallback if pagination info not available
          setMostVisitedTotal(mostVisitedData.length);
          setMostVisitedHasMore(mostVisitedData.length >= mostVisitedLimit);
        }

        console.log('✅ Activity from screenshots data fetched:', mostVisitedData.length, 'items');
        console.log('📊 Pagination info:', {
          page: mostVisitedPage,
          limit: mostVisitedLimit,
          total: pagination?.total || mostVisitedData.length,
          hasMore: pagination?.hasMore || false
        });

        if (mostVisitedData.length === 0) {
          console.log('⚠️ No most visited data returned from API');
          console.log('📊 API summary:', response.data.data.summary);
        } else {
          console.log('📊 Activity from screenshots data:', mostVisitedData.slice(0, 3)); // Show first 3 items
        }
      } else {
        console.warn('❌ Activity from screenshots API returned success=false:', response.data);
        setMostVisited([]);
        setMostVisitedDebug(response.data);
      }
    } catch (error) {
      console.error('❌ Activity from screenshots API failed:', error.message);
      console.error('❌ Error details:', error.response?.data);
      // Show empty state when API fails
      setMostVisited([]);
    } finally {
      setMostVisitedLoading(false);
    }
  }, [startDate, endDate, selectedTeamMember, mostVisitedPage, mostVisitedLimit]);

  // Fetch hours data for logged-in user + team
  const fetchHoursData = useCallback(async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      if (startDate) {
        const startDateStr = formatDateForAPI(startDate, false);
        params.append('startDate', startDateStr ? startDateStr.split('T')[0] : '');
      }
      if (endDate) {
        const endDateStr = formatDateForAPI(endDate, true);
        params.append('endDate', endDateStr ? endDateStr.split('T')[0] : '');
      }

      let endpoint = '/api/v1/live-analytics/team-hours';

      // If a specific team member is selected, use the individual endpoint
      if (selectedTeamMember) {
        endpoint = `/api/v1/live-analytics/today-hours?userId=${selectedTeamMember}&${params.toString()}`;
      } else {
        // Use team-hours endpoint with date params
        endpoint = `/api/v1/live-analytics/team-hours?${params.toString()}`;
      }

      console.log('🔍 Fetching hours data:', {
        endpoint,
        selectedTeamMember,
        startDate: startDate?.toISOString().split('T')[0],
        endDate: endDate?.toISOString().split('T')[0],
        params: params.toString()
      });

      const response = await api.get(endpoint);

      console.log('📡 Hours API response:', {
        status: response.status,
        success: response.data.success,
        data: response.data.data,
        endpoint
      });

      if (response.data.success) {
        const data = response.data.data;

        // Calculate active time if not provided (for backward compatibility)
        let activeTime = data.totalActiveTimeFormatted || '0h 0m';
        let idleTime = data.totalIdleTimeFormatted || '0h 0m';

        // Fallback calculation if API doesn't provide formatted times
        if (!data.totalActiveTimeFormatted && data.totalMinutes && data.totalIdleTimeMinutes !== undefined) {
          const activeMinutes = Math.max(0, data.totalMinutes - data.totalIdleTimeMinutes);
          const activeHours = Math.floor(activeMinutes / 60);
          const activeMins = activeMinutes % 60;
          activeTime = `${activeHours}h ${activeMins}m`;
        }

        setHoursData({
          totalHours: data.formattedTime || '0h 0m',
          totalSessions: data.totalSessions || data.sessionsCount || 0,
          idleTime: idleTime,
          activeTime: activeTime
        });

        console.log('✅ Hours calculated and set:', {
          totalHours: data.formattedTime,
          totalSessions: data.totalSessions || data.sessionsCount || 0,
          idleTime: idleTime,
          activeTime: activeTime,
          selectedTeamMember,
          userId: data.userId,
          filters: { selectedTeamMember, startDate, endDate }
        });
      } else {
        console.error('❌ Hours API failed:', response.data);
      }

    } catch (error) {
      console.error('Error fetching hours data:', error);
      setHoursData({
        totalHours: '0h 0m',
        totalSessions: 0,
        idleTime: '0h 0m',
        activeTime: '0h 0m'
      });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedTeamMember, api]);

  // Fetch team members on component mount and when user changes
  useEffect(() => {
    if (user) {
      fetchTeamMembers();
    }
  }, [user]);

  // Reset pagination when filters change
  useEffect(() => {
    resetPagination();
  }, [selectedTeamMember, startDate, endDate]);

  // Fetch goals team stats on mount (S5 org only)
  useEffect(() => {
    if (isS5) fetchGoalsTeamStats();
  }, [isS5, fetchGoalsTeamStats]);

  // Debug function to test user projects
  const debugUserProjects = async (userId) => {
    console.log('🔍 Debug: Testing user projects for:', userId);

    try {
      // Test 1: Check if user exists in team members
      const teamMembersResponse = await api.get('/api/v1/users/team-members');
      const teamMembers = teamMembersResponse.data.data || [];
      const userExists = teamMembers.find(member => member._id === userId);

      console.log('🔍 Debug Test 1 - User exists in team:', userExists ? 'YES' : 'NO');
      if (userExists) {
        console.log('   User details:', userExists);
      }

      // Test 2: Try user-specific projects endpoint
      try {
        const userProjectsResponse = await api.get(`/api/v1/users/${userId}/projects`);
        console.log('🔍 Debug Test 2 - User projects endpoint:', userProjectsResponse.data);
      } catch (error) {
        console.log('🔍 Debug Test 2 - User projects endpoint failed:', error.message);
      }

      // Test 3: Check projects dropdown with teamMembers
      const projectsResponse = await api.get('/api/v1/projects/dropdown');
      const projects = projectsResponse.data.data.projects || [];

      console.log('🔍 Debug Test 3 - Projects dropdown structure:');
      console.log('   Total projects:', projects.length);

      if (projects.length > 0) {
        const sampleProject = projects[0];
        console.log('   Sample project teamMembers:', sampleProject.teamMembers);

        // Check if any project has this user
        const projectsWithUser = projects.filter(project => {
          if (Array.isArray(project.teamMembers)) {
            return project.teamMembers.some(member => {
              const memberId = member._id || member.userId?._id || member.userId || member;
              return memberId === userId || memberId?.toString() === userId;
            });
          }
          return false;
        });

        console.log('   Projects with this user:', projectsWithUser.length);
        projectsWithUser.forEach(p => console.log('     -', p.name));
      }

    } catch (error) {
      console.error('🔍 Debug failed:', error);
    }
  };

  // Expose debug function globally for testing
  window.debugUserProjects = debugUserProjects;

  // Fetch all data when user and dates are available (including initial load)
  useEffect(() => {
    if (user && startDate && endDate) {
      console.log('🚀 Fetching dashboard data for:', {
        user: user.email,
        startDate: formatDateForAPI(startDate, false)?.split('T')[0] || '',
        endDate: formatDateForAPI(endDate, true)?.split('T')[0] || '',
        selectedTeamMember
      });
      fetchProductivityData();
      fetchTeamAnalytics();
      fetchProjectOverview();
      fetchMostVisited();
      fetchTeamAverageScore();
      fetchHoursData();
    }
  }, [user, startDate, endDate, selectedTeamMember]); // Trigger when any filter changes

  // Reset pagination when filters change (date range or team member)
  useEffect(() => {
    setTeamPage(1);
    setProjectsPage(1);
  }, [startDate, endDate, selectedTeamMember]);

  // Fetch team analytics when pagination changes (server-side pagination)
  useEffect(() => {
    if (user && startDate && endDate) {
      fetchTeamAnalytics();
    }
  }, [teamPage, fetchTeamAnalytics]);

  // Fetch projects when pagination changes (server-side pagination)
  useEffect(() => {
    if (user && startDate && endDate) {
      fetchProjectOverview();
    }
  }, [projectsPage, fetchProjectOverview]);

  // Fetch most visited when pagination changes (server-side pagination)
  useEffect(() => {
    if (user && startDate && endDate) {
      fetchMostVisited();
    }
  }, [mostVisitedPage, mostVisitedLimit]);


  // Helper function to format time safely
  const formatTimeSafely = (timeSpent, formattedTime) => {
    // If we already have a formatted time string, use it
    if (formattedTime) {
      return formattedTime;
    }

    // If timeSpent is already a string (like "2h 30m"), return it
    if (typeof timeSpent === 'string') {
      return timeSpent;
    }

    // If no valid timeSpent value, return default
    if (!timeSpent || isNaN(timeSpent) || timeSpent <= 0) {
      return '0m';
    }

    // Convert seconds to hours and minutes format
    const hours = Math.floor(timeSpent / 3600);
    const minutes = Math.floor((timeSpent % 3600) / 60);
    const seconds = Math.floor(timeSpent % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  // Helper function to convert time string to hours (e.g., "5h 30m" -> 5.5)
  const parseTimeToHours = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return 0;
    const hoursMatch = timeStr.match(/(\d+)h/);
    const minutesMatch = timeStr.match(/(\d+)m/);
    const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
    return hours + (minutes / 60);
  };

  // Chart Data Preparation Functions for Mini Graphs using ApexCharts

  // Graph 1: Time Tracking - Donut Chart (Active, Total, Idle Hours)
  const timeTrackingChartData = useMemo(() => {
    const activeHours = parseTimeToHours(hoursData.activeTime) || 0;
    const totalHours = parseTimeToHours(hoursData.totalHours) || 0;
    const idleHours = parseTimeToHours(hoursData.idleTime) || 0;

    return {
      series: [activeHours, totalHours, idleHours],
      labels: ['Active Hours', 'Total Hours', 'Idle Hours'],
      colors: ['#3b82f6', '#10b981', '#fb923c']
    };
  }, [hoursData]);

  // Graph 2: Team Members - Gauge chart (Active vs Total)
  const teamMembersChartData = useMemo(() => {
    const totalMembers = teamStats.totalMembers || 0;
    const activeMembers = teamStats.activeMembers || 0;
    const percentage = totalMembers > 0 ? Math.round((activeMembers / totalMembers) * 100) : 0;

    return {
      percentage: percentage,
      activeMembers: activeMembers,
      totalMembers: totalMembers
    };
  }, [teamStats]);

  // Graph 3: Team Average - Radial Progress Chart
  const teamAverageChartData = useMemo(() => {
    const baseProductivity = teamAverageData.teamAverage || 0;

    return {
      percentage: Math.min(100, Math.max(0, baseProductivity))
    };
  }, [teamAverageData]);

  // Graph 4: Projects - Concentric Donut (Double Ring)
  const projectsChartData = useMemo(() => {
    const active = projectsData.activeProjects || 0;
    const completed = projectsData.completedProjects || 0;
    const archived = projectsData.archivedProjects || 0;
    const total = projectsData.totalProjects || 0;
    const inactive = total - active - completed - archived;

    // Inner ring: Active vs Inactive ratio
    const innerActivePercentage = total > 0 ? Math.round((active / total) * 100) : 0;
    const innerInactivePercentage = 100 - innerActivePercentage;

    // Outer ring: Total projects visualization (segmented for visual appeal)
    // We'll show it as a full circle representing total
    const outerTotal = total;
    const maxForVisualization = Math.max(100, total); // Scale for visualization

    return {
      // Inner ring data
      innerRing: {
        series: [innerActivePercentage, innerInactivePercentage],
        labels: ['Active', 'Inactive'],
        colors: ['#10b981', '#6b7280']
      },
      // Outer ring data
      outerRing: {
        total: total,
        active: active,
        inactive: inactive
      },
      // Combined for single chart approach
      categories: ['Active', 'Inactive'],
      series: [active, inactive],
      colors: ['#10b981', '#6b7280'],
      total: total
    };
  }, [projectsData]);

  return (
    <PageAnimationWrapper className={`transition-all duration-300 overflow-x-hidden ${theme === 'dark'
      ? 'text-white'
      : 'bg-gray-50 text-gray-900'
      }`}>
      <style>{customDatepickerStyles}</style>
      <style>{`
        @media (min-width: 1200px) {
          .goals-metrics-row.ant-row {
            display: flex !important;
            flex-wrap: nowrap !important;
          }
          .goals-metrics-row .goals-metrics-col {
            flex: 1 1 0% !important;
            max-width: none !important;
            width: 0 !important;
          }
        }
      `}</style>
      <div className="w-full px-4 py-4 max-w-full" style={{ overflowX: 'hidden' }}>
        {overlay}

        {/* Header Section - Compact (BrainOps Style) */}
        <div className="mb-4">
          <div className="flex items-center justify-between py-3 border-b border-zinc-800/50">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg border ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-gray-100 border-gray-200'
                }`}>
                <BarChart3 className={`h-5 w-5 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`} />
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Dashboard</h1>
                <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Team Performance • Real-time Analytics</p>
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-3">
              {/* Free Trial Countdown Banner */}
              {trialDaysRemaining !== null && (
                <>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
                    isDark 
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' 
                      : 'bg-amber-50 border-amber-200 text-amber-700'
                  }`}>
                    <Clock className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      Free trial ends in {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      if (user?.role === 'org_user') {
                        toast.error('You don\'t have permission, contact organization admin');
                      } else if (user?.role === 'org_admin') {
                        navigate('/dashboard/settings?section=billing');
                      }
                    }}
                    className={`group flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 shadow-sm ${
                      isDark 
                        ? 'bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/40 text-amber-300 hover:from-amber-500/30 hover:to-amber-600/30 hover:border-amber-500/60 hover:shadow-amber-500/20 hover:shadow-md' 
                        : 'bg-gradient-to-r from-amber-50 to-amber-100/80 border border-amber-300/60 text-amber-800 hover:from-amber-100 hover:to-amber-200 hover:border-amber-400 hover:shadow-md'
                    }`}
                  >
                    <Crown className={`h-4 w-4 transition-transform duration-200 ${isDark ? 'text-amber-400' : 'text-amber-600'} group-hover:scale-110`} />
                    <span>Upgrade to Paid Plan</span>
                  </button>
                </>
              )}
              
              <div className="text-right">
                <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
                <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'} uppercase tracking-wider`}>
                  {new Date().toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
              </div>

              {/* Filter Button - BrainOps Style */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200 ${isDark
                  ? `bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700 ${showFilters ? 'bg-zinc-800 border-zinc-700' : ''
                  }`
                  : `bg-white border border-gray-200 hover:bg-gray-50 ${showFilters ? 'bg-gray-100 border-gray-300' : ''
                  }`
                  }`}
                title={showFilters ? 'Hide Filters' : 'Show Filters'}
              >
                <Filter className={`h-4 w-4 transition-colors ${showFilters
                  ? (isDark ? 'text-blue-400' : 'text-blue-600')
                  : (isDark ? 'text-zinc-400' : 'text-gray-500')
                  }`} />
                <span className={`text-sm font-semibold transition-colors ${showFilters
                  ? (isDark ? 'text-blue-400' : 'text-blue-600')
                  : (isDark ? 'text-zinc-400' : 'text-gray-500')
                  }`}>
                  Filter
                </span>
              </button>

              <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-gray-100 border-gray-200'
                }`}>
                <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {user?.firstName?.charAt(0) || 'U'}{user?.lastName?.charAt(0) || 'S'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Admin-only: Gmail connection status across the organization */}
        {(user?.role === 'org_admin' || user?.role === 'main_admin') && (
          <OrgGmailStatsCard isDark={isDark} />
        )}

        {/* Payment Success Banner */}
        {paymentSuccess && selectedPlan && (
          <div className="mb-4 p-3 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-green-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">Payment Successful!</h3>
                <p className="text-green-300 text-sm">
                  Welcome to <span className="font-semibold capitalize">{selectedPlan}</span> plan.
                  Your account is now activated and you have full access to all features.
                </p>
              </div>
              <button
                onClick={() => setPaymentSuccess(false)}
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Enhanced Filters Section - Toggleable */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className={`backdrop-blur-sm border rounded-xl p-3 mb-4 overflow-hidden ${theme === 'dark'
                ? 'bg-white/5 border-white/10'
                : 'bg-white/80 border-gray-200'
                }`}
            >
              <div className="flex items-center space-x-2 mb-3">
                <TrendingUp className={`h-4 w-4 ${theme === 'dark' ? 'text-zinc-500' : 'text-gray-500'}`} />
                <h3 className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Data Filters</h3>
                <div className="group relative">
                  <Info className="h-4 w-4 text-blue-400 cursor-help" />
                </div>
              </div>
              <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-3">

                {/* Team Member Filter - Custom Dropdown */}
                <div className="flex-1 min-w-0">
                  <label className={`block text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Team Member</label>
                  <AntDropdown
                    options={[
                      { value: '', label: 'All Team Members' },
                      ...teamMembers.map(member => ({
                        value: member._id,
                        label: `${member.firstName} ${member.lastName}`
                      }))
                    ]}
                    value={selectedTeamMember}
                    onChange={(value) => {
                      console.log('AntDropdown onChange called with:', value);
                      setSelectedTeamMember(value);
                    }}
                    placeholder="All Team Members"
                    showSearch={true}
                    allowClear={true}
                  />
                </div>

                {/* Date Range Filter - Compact */}
                <div className="flex-1 min-w-0">
                  <label className={`block text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Date Range</label>
                  <div className="relative">
                    <AntRangePicker
                      value={startDate && endDate ? [startDate, endDate] : null}
                      onChange={(dates) => {
                        if (dates && Array.isArray(dates)) {
                          setDateRange(dates);
                        }
                      }}
                      placeholder={['Start date', 'End date']}
                      showPresets={true}
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Clear Button */}
                <button
                  onClick={() => {
                    setSelectedTeamMember('');
                    const today = new Date();
                    setDateRange([today, today]);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${theme === 'dark'
                    ? 'bg-white/10 hover:bg-white/20 border border-white/20 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700'
                    }`}
                >
                  Clear
                </button>
              </div>

              {/* Quick Filter Buttons */}
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => {
                    const today = new Date();
                    setDateRange([today, today]);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isSameDay(startDate, new Date()) && isSameDay(endDate, new Date())
                    ? (theme === 'dark' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-100 text-blue-700 border border-blue-300')
                    : (theme === 'dark' ? 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200')
                    }`}
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    const yesterday = getYesterday();
                    setDateRange([yesterday, yesterday]);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isSameDay(startDate, getYesterday()) && isSameDay(endDate, getYesterday())
                    ? (theme === 'dark' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-100 text-blue-700 border border-blue-300')
                    : (theme === 'dark' ? 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200')
                    }`}
                >
                  Yesterday
                </button>
                <button
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - 6);
                    setDateRange([start, end]);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${getDaysDifference(startDate, endDate) === 6
                    ? (theme === 'dark' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-100 text-blue-700 border border-blue-300')
                    : (theme === 'dark' ? 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200')
                    }`}
                >
                  7 Days
                </button>
                <button
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - 29);
                    setDateRange([start, end]);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${getDaysDifference(startDate, endDate) === 29
                    ? (theme === 'dark' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-blue-100 text-blue-700 border border-blue-300')
                    : (theme === 'dark' ? 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200')
                    }`}
                >
                  30 Days
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Key Metrics Cards - Enhanced with Help */}
        <div className="mb-2">
          <div className="flex items-center space-x-2">
            <h3 className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Key Metrics</h3>
            <div className="group/tooltip relative inline-block">
              <Lightbulb className="h-3 w-3 text-yellow-400 cursor-help" />
              <div className="absolute left-0 top-6 bg-black/90 border border-white/20 rounded-lg p-3 text-xs text-white w-80 opacity-0 group-hover/tooltip:opacity-100 transition-opacity z-50 shadow-xl pointer-events-none">
                <div className="font-semibold text-yellow-400 mb-2">📊 Understanding Your Metrics</div>
                <ul className="space-y-1 text-gray-300">
                  <li>• <strong>Total Hours:</strong> Combined work time across all sessions</li>
                  <li>• <strong>Team Hours:</strong> Collective time worked by your team</li>
                  <li>• <strong>Team Average:</strong> Average productivity score across team members</li>
                  <li>• <strong>Active Projects:</strong> Number of projects currently being worked on</li>
                </ul>
              </div>
            </div>
          </div>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Real-time performance indicators</p>
        </div>
        <Row
          gutter={[8, 8]}
          className="mb-3 goals-metrics-row"
        >
          {/* 5 cards - responsive: 1 per row on xs, 2 per row on sm, 5 per row on xl+ (equal width) */}
          {(() => {
            return (
              <>
                {/* Total Hours Card */}
                <Col
                  xs={24}
                  sm={12}
                  lg={8}
                  xl={4}
                  style={{ display: 'flex' }}
                  className="goals-metrics-col"
                >
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    whileHover={cardHoverVariants.hover}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <Card
                      className="h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
                      hoverable
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
                        borderRadius: '12px'
                      }}
                      bodyStyle={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '12px' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 ${isDark
                          ? 'bg-blue-500/20 border border-blue-500/30'
                          : 'bg-blue-100 border border-blue-200'
                          }`}>
                          <Clock className={`h-4 w-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">Hours</div>
                          <div className="group relative">
                            {/* <HelpCircle className="h-3 w-3 text-gray-500 cursor-help" /> */}
                            {/* <div className="absolute right-0 top-4 bg-black/90 border border-white/20 rounded-lg p-2 text-xs text-white w-48 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                    <p><strong>Total Hours:</strong> Sum of all tracked work time including active sessions and completed tasks.</p>
                  </div> */}
                          </div>
                        </div>
                      </div>
                      {loading ? (
                        <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                          {/* Custom Skeleton for Active Hours Card */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px',
                            marginTop: '4px',
                            width: '100%',
                            maxWidth: '100%'
                          }}>
                            {/* Text Skeleton on Left */}
                            <div style={{ flex: '1 1 auto', minWidth: 0, maxWidth: '60%' }}>
                              <Skeleton.Input
                                active
                                size="small"
                                style={{ width: '70px', maxWidth: '100%', height: '13px', marginBottom: '6px' }}
                              />
                              <Skeleton.Input
                                active
                                style={{ width: '90px', maxWidth: '100%', height: '24px' }}
                              />
                            </div>

                            {/* Donut Chart Skeleton on Right */}
                            <div style={{
                              width: '100px',
                              maxWidth: '40%',
                              height: '100px',
                              flexShrink: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}>
                              <Skeleton.Avatar
                                active
                                shape="circle"
                                size={100}
                                style={{ borderRadius: '50%', maxWidth: '100%', height: 'auto' }}
                              />
                            </div>
                          </div>

                          {/* Legend Skeleton */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '8px',
                            width: '100%',
                            maxWidth: '100%',
                            marginTop: '8px'
                          }}>
                            {[1, 2].map((item) => (
                              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                <Skeleton.Avatar active size={8} shape="circle" />
                                <Skeleton.Input active size="small" style={{ width: '60px', maxWidth: '100%', height: '10px' }} />
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Active Hours Text and Donut Chart in Same Row */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px',
                            marginTop: '4px'
                          }}>
                            {/* Active Hours Text on Left */}
                            <div style={{ flexShrink: 0 }}>
                              <Statistic
                                title={<span style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Active Hours</span>}
                                value={hoursData.activeTime}
                                valueStyle={{ color: '#3b82f6', fontSize: '24px', fontWeight: 'bold' }}
                                style={{ margin: 0 }}
                              />
                            </div>

                            {/* Donut Chart on Right */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: '100px',
                              height: '100px',
                              flexShrink: 0
                            }}>
                              <Chart
                                type="donut"
                                height={100}
                                series={timeTrackingChartData.series}
                                options={{
                                  chart: {
                                    type: 'donut',
                                    height: 100,
                                    toolbar: { show: false },
                                    animations: { enabled: true, speed: 800 }
                                  },
                                  labels: timeTrackingChartData.labels,
                                  colors: timeTrackingChartData.colors,
                                  plotOptions: {
                                    pie: {
                                      donut: {
                                        size: '65%',
                                        labels: {
                                          show: false
                                        }
                                      }
                                    }
                                  },
                                  dataLabels: { enabled: false },
                                  stroke: {
                                    show: true,
                                    width: 2,
                                    colors: [isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff']
                                  },
                                  tooltip: {
                                    enabled: true,
                                    theme: isDark ? 'dark' : 'light',
                                    custom: function ({ series, seriesIndex, w }) {
                                      const value = series[seriesIndex];
                                      const label = timeTrackingChartData.labels[seriesIndex];
                                      const color = timeTrackingChartData.colors[seriesIndex];
                                      const hours = Math.floor(value);
                                      const minutes = Math.round((value - hours) * 60);
                                      const displayValue = hours > 0
                                        ? `${hours}h ${minutes > 0 ? minutes + 'm' : ''}`.trim()
                                        : `${minutes}m`;
                                      return `
                                  <div style="
                                    background: ${isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff'};
                                    border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'};
                                    border-radius: 8px;
                                    padding: 8px 12px;
                                    font-size: 12px;
                                  ">
                                    <div style="display: flex; align-items: center; gap: 8px;">
                                      <div style="
                                        width: 12px;
                                        height: 12px;
                                        border-radius: 50%;
                                        background: ${color};
                                      "></div>
                                      <div>
                                        <div style="color: ${isDark ? '#9ca3af' : '#6b7280'}; font-size: 11px; margin-bottom: 2px;">${label}</div>
                                        <div style="color: ${color}; font-weight: 600;">${displayValue}</div>
                                      </div>
                                    </div>
                                  </div>
                                `;
                                    }
                                  },
                                  legend: { show: false }
                                }}
                              />
                            </div>
                          </div>

                          {/* Legend Below - Horizontal Layout */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '12px',
                            width: '100%',
                            marginTop: '8px'
                          }}>
                            {timeTrackingChartData.labels
                              .filter(label => label !== 'Active Hours')
                              .map((label) => {
                                const index = timeTrackingChartData.labels.indexOf(label);
                                const value = timeTrackingChartData.series[index] || 0;
                                const hours = Math.floor(value);
                                const minutes = Math.round((value - hours) * 60);
                                const displayValue = hours > 0
                                  ? `${hours}h ${minutes > 0 ? minutes + 'm' : ''}`.trim()
                                  : `${minutes}m`;
                                return (
                                  <div
                                    key={label}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      fontSize: '10px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: timeTrackingChartData.colors[index],
                                        flexShrink: 0
                                      }}
                                    />
                                    <span style={{
                                      color: isDark ? '#d1d5db' : '#374151',
                                      fontWeight: 500,
                                      // textDecoration: isIdle ? 'underline dotted' : 'none',
                                    }}>
                                      {label}
                                    </span>
                                    <span style={{
                                      color: isDark ? '#9ca3af' : '#6b7280',
                                      marginLeft: '2px',
                                      fontSize: '10px'
                                    }}>
                                      {displayValue}
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                        </>
                      )}
                    </Card>
                  </motion.div>
                </Col>

                {/* Team Members Card */}
                <Col
                  xs={24}
                  sm={12}
                  lg={8}
                  xl={4}
                  style={{ display: 'flex' }}
                  className="goals-metrics-col"
                >
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    custom={1}
                    whileHover={cardHoverVariants.hover}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <Card
                      className="h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer"
                      hoverable
                      onClick={() => window.open('/dashboard/team', '_blank')}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
                        borderRadius: '12px'
                      }}
                      bodyStyle={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '8px' }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 ${isDark
                          ? 'bg-green-500/20 border border-green-500/30'
                          : 'bg-green-100 border border-green-200'
                          }`}>
                          <Users className={`h-4 w-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                            {selectedTeamMember ? 'Member' : 'Team'}
                          </div>
                          <div className="group relative">
                            <HelpCircle className="h-3 w-3 text-gray-500 cursor-help" />
                            <div className="absolute right-0 top-4 bg-black/90 border border-white/20 rounded-lg p-2 text-xs text-white w-48 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                              <p><strong>Team Size:</strong> Total number of team members you can monitor and analyze.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      {loading ? (
                        <>
                          {/* Custom Skeleton for Team Members Card */}
                          <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '6px',
                              marginTop: '4px',
                              width: '100%',
                              maxWidth: '100%'
                            }}>
                              {/* Text Skeleton on Left */}
                              <div style={{ flex: '1 1 auto', minWidth: 0, maxWidth: '60%' }}>
                                <Skeleton.Input
                                  active
                                  size="small"
                                  style={{ width: '85px', maxWidth: '100%', height: '13px', marginBottom: '6px' }}
                                />
                                <Skeleton.Input
                                  active
                                  style={{ width: '45px', maxWidth: '100%', height: '24px' }}
                                />
                              </div>

                              {/* Gauge Chart Skeleton on Right */}
                              <div style={{
                                width: '100px',
                                maxWidth: '40%',
                                height: '100px',
                                flexShrink: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}>
                                <Skeleton.Avatar
                                  active
                                  shape="circle"
                                  size={100}
                                  style={{ borderRadius: '50%', maxWidth: '100%', height: 'auto' }}
                                />
                              </div>
                            </div>

                            {/* Legend Skeleton */}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '8px',
                              width: '100%',
                              maxWidth: '100%',
                              marginTop: '8px'
                            }}>
                              {[1, 2].map((item) => (
                                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                  <Skeleton.Avatar active size={8} shape="circle" />
                                  <Skeleton.Input active size="small" style={{ width: '60px', maxWidth: '100%', height: '10px' }} />
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Team Members Text and Gauge Chart in Same Row */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px',
                            marginTop: '4px'
                          }}>
                            {/* Team Members Text on Left */}
                            <div style={{ flexShrink: 0 }}>
                              <Statistic
                                title={<span style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>{selectedTeamMember ? 'Selected Member' : 'Team Members'}</span>}
                                value={selectedTeamMember ? 1 : teamStats.totalMembers}
                                valueStyle={{ color: '#10b981', fontSize: '24px', fontWeight: 'bold' }}
                                style={{ margin: 0 }}
                              />
                            </div>

                            {/* Gauge Chart on Right */}
                            <div style={{
                              width: '100px',
                              height: '100px',
                              flexShrink: 0
                            }}>
                              <Chart
                                type="radialBar"
                                height={100}
                                series={[teamMembersChartData.percentage]}
                                options={{
                                  chart: {
                                    type: 'radialBar',
                                    height: 100,
                                    toolbar: { show: false },
                                    animations: { enabled: true, speed: 1000, easing: 'easeout' },
                                    sparkline: { enabled: false }
                                  },
                                  plotOptions: {
                                    radialBar: {
                                      startAngle: -90,
                                      endAngle: 90,
                                      hollow: {
                                        margin: 0,
                                        size: '75%',
                                        background: 'transparent',
                                        image: undefined,
                                        position: 'front',
                                        dropShadow: {
                                          enabled: false
                                        }
                                      },
                                      track: {
                                        background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                        strokeWidth: '70%',
                                        margin: 0,
                                        dropShadow: {
                                          enabled: false
                                        }
                                      },
                                      dataLabels: {
                                        show: true,
                                        name: {
                                          show: false
                                        },
                                        value: {
                                          show: true,
                                          fontSize: '20px',
                                          fontWeight: 700,
                                          color: '#10b981',
                                          offsetY: 0,
                                          fontFamily: 'inherit',
                                          formatter: function (val) {
                                            return val + '%';
                                          }
                                        }
                                      }
                                    }
                                  },
                                  fill: {
                                    type: 'gradient',
                                    gradient: {
                                      shade: 'dark',
                                      type: 'horizontal',
                                      shadeIntensity: 0.5,
                                      gradientToColors: ['#22c55e'],
                                      inverseColors: true,
                                      opacityFrom: 1,
                                      opacityTo: 0.9,
                                      stops: [0, 100],
                                      colorStops: [
                                        {
                                          offset: 0,
                                          color: '#10b981',
                                          opacity: 1
                                        },
                                        {
                                          offset: 100,
                                          color: '#22c55e',
                                          opacity: 0.9
                                        }
                                      ]
                                    }
                                  },
                                  colors: ['#10b981'],
                                  stroke: {
                                    lineCap: 'round',
                                    curve: 'smooth'
                                  },
                                  tooltip: {
                                    enabled: true,
                                    theme: isDark ? 'dark' : 'light',
                                    custom: function ({ series, seriesIndex }) {
                                      return `
                                  <div style="
                                    background: ${isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff'};
                                    border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'};
                                    border-radius: 8px;
                                    padding: 10px 14px;
                                    font-size: 12px;
                                    box-shadow: ${isDark ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.1)'};
                                  ">
                                    <div style="color: ${isDark ? '#9ca3af' : '#6b7280'}; font-size: 11px; margin-bottom: 6px; font-weight: 500;">Active Members</div>
                                    <div style="color: #10b981; font-weight: 700; font-size: 16px; margin-bottom: 4px;">
                                      ${teamMembersChartData.activeMembers} / ${teamMembersChartData.totalMembers}
                                    </div>
                                    <div style="color: ${isDark ? '#9ca3af' : '#6b7280'}; font-size: 11px;">
                                      ${series[seriesIndex]}% Active
                                    </div>
                                  </div>
                                `;
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {/* Legend Below - Horizontal Layout */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '12px',
                            width: '100%',
                            marginTop: '8px'
                          }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '10px'
                              }}
                            >
                              <div
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: '#10b981',
                                  flexShrink: 0
                                }}
                              />
                              <span style={{
                                color: isDark ? '#d1d5db' : '#374151',
                                fontWeight: 500
                              }}>
                                Active
                              </span>
                              <span style={{
                                color: isDark ? '#9ca3af' : '#6b7280',
                                marginLeft: '2px',
                                fontSize: '10px'
                              }}>
                                {teamMembersChartData.activeMembers}
                              </span>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '10px'
                              }}
                            >
                              <div
                                style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#e5e7eb',
                                  flexShrink: 0
                                }}
                              />
                              <span style={{
                                color: isDark ? '#d1d5db' : '#374151',
                                fontWeight: 500
                              }}>
                                Total
                              </span>
                              <span style={{
                                color: isDark ? '#9ca3af' : '#6b7280',
                                marginLeft: '2px',
                                fontSize: '10px'
                              }}>
                                {teamMembersChartData.totalMembers}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </Card>
                  </motion.div>
                </Col>

                {/* Team Average Score Card (DailySummary) */}
                {false && (
                  <Col xs={24} sm={12} xl={4} style={{ display: 'flex' }}>
                    <motion.div
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      custom={2}
                      whileHover={cardHoverVariants.hover}
                      style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                      <Card
                        className="h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer"
                        hoverable
                        onClick={() => window.open('/dashboard/daily-summary', '_blank')}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
                          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
                          borderRadius: '12px'
                        }}
                        bodyStyle={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '8px' }}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 ${isDark
                            ? 'bg-purple-500/20 border border-purple-500/30'
                            : 'bg-purple-100 border border-purple-200'
                            }`}>
                            <Target className={`h-4 w-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                          </div>
                          <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                            Team Avg
                          </div>
                        </div>
                        {loading ? (
                          <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '6px',
                              marginTop: '4px',
                              width: '100%',
                              maxWidth: '100%'
                            }}>

                              <div style={{ flex: '1 1 auto', minWidth: 0, maxWidth: '60%' }}>
                                <Skeleton.Input
                                  active
                                  size="small"
                                  style={{ width: '90px', maxWidth: '100%', height: '13px', marginBottom: '6px' }}
                                />
                                <Skeleton.Input
                                  active
                                  style={{ width: '50px', maxWidth: '100%', height: '24px' }}
                                />
                              </div>


                              <div style={{
                                width: '100px',
                                maxWidth: '40%',
                                height: '100px',
                                flexShrink: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}>
                                <Skeleton.Avatar
                                  active
                                  shape="circle"
                                  size={100}
                                  style={{ borderRadius: '50%', maxWidth: '100%', height: 'auto' }}
                                />
                              </div>
                            </div>


                            <div style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '8px',
                              width: '100%',
                              maxWidth: '100%',
                              marginTop: '8px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                <Skeleton.Avatar active size={8} shape="circle" />
                                <Skeleton.Input active size="small" style={{ width: '80px', maxWidth: '100%', height: '10px' }} />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>

                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '6px',
                              marginTop: '4px'
                            }}>

                              <div style={{ flexShrink: 0 }}>
                                <Statistic
                                  title={<span style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>{selectedTeamMember ? 'Member Score' : 'Team Average'}</span>}
                                  value={teamAverageData.isIndividualScore && teamAverageData.teamAverage === 0 ? 0 : teamAverageData.teamAverage}
                                  suffix="%"
                                  valueStyle={{ color: '#a855f7', fontSize: '24px', fontWeight: 'bold' }}
                                  style={{ margin: 0 }}
                                />
                              </div>


                              <div style={{
                                width: '100px',
                                height: '100px',
                                flexShrink: 0
                              }}>
                                <Chart
                                  type="radialBar"
                                  height={100}
                                  series={[teamAverageChartData.percentage]}
                                  options={{
                                    chart: {
                                      type: 'radialBar',
                                      height: 100,
                                      toolbar: { show: false },
                                      animations: { enabled: true, speed: 1000, easing: 'easeout' }
                                    },
                                    plotOptions: {
                                      radialBar: {
                                        startAngle: 0,
                                        endAngle: 360,
                                        hollow: {
                                          margin: 0,
                                          size: '70%',
                                          background: 'transparent',
                                          dropShadow: {
                                            enabled: false
                                          }
                                        },
                                        track: {
                                          background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                          strokeWidth: '100%',
                                          margin: 0
                                        },
                                        dataLabels: {
                                          show: true,
                                          name: {
                                            show: false
                                          },
                                          value: {
                                            show: true,
                                            fontSize: '20px',
                                            fontWeight: 700,
                                            color: '#a855f7',
                                            offsetY: 0,
                                            fontFamily: 'inherit',
                                            formatter: function (val) {
                                              return val.toFixed(0) + '%';
                                            }
                                          }
                                        }
                                      }
                                    },
                                    fill: {
                                      type: 'gradient',
                                      gradient: {
                                        shade: 'light',
                                        type: 'vertical',
                                        shadeIntensity: 0.3,
                                        gradientToColors: ['#c084fc'],
                                        inverseColors: false,
                                        opacityFrom: 1,
                                        opacityTo: 0.9,
                                        stops: [0, 100]
                                      }
                                    },
                                    colors: ['#a855f7'],
                                    stroke: {
                                      lineCap: 'round',
                                      curve: 'smooth'
                                    },
                                    tooltip: {
                                      enabled: true,
                                      theme: isDark ? 'dark' : 'light',
                                      custom: function ({ series, seriesIndex }) {
                                        return `
                                    <div style="
                                      background: ${isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff'};
                                      border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'};
                                      border-radius: 8px;
                                      padding: 10px 14px;
                                      font-size: 12px;
                                      box-shadow: ${isDark ? '0 4px 6px rgba(0, 0, 0, 0.3)' : '0 4px 6px rgba(0, 0, 0, 0.1)'};
                                    ">
                                      <div style="color: ${isDark ? '#9ca3af' : '#6b7280'}; font-size: 11px; margin-bottom: 6px; font-weight: 500;">Team Average</div>
                                      <div style="color: #a855f7; font-weight: 700; font-size: 16px; margin-bottom: 4px;">
                                        ${series[seriesIndex].toFixed(1)}%
                                      </div>
                                      ${teamAverageData.userScore > 0 ? `
                                        <div style="color: ${isDark ? '#9ca3af' : '#6b7280'}; font-size: 11px; margin-top: 4px; border-top: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}; padding-top: 4px;">
                                          Your Score: ${teamAverageData.userScore}%
                                        </div>
                                      ` : ''}
                                    </div>
                                  `;
                                      }
                                    }
                                  }}
                                />
                              </div>
                            </div>


                            <div style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '12px',
                              width: '100%',
                              marginTop: '8px'
                            }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '10px'
                                }}
                              >
                                <div
                                  style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#a855f7',
                                    flexShrink: 0
                                  }}
                                />
                                <span style={{
                                  color: isDark ? '#d1d5db' : '#374151',
                                  fontWeight: 500
                                }}>
                                  Team Average
                                </span>
                                <span style={{
                                  color: isDark ? '#9ca3af' : '#6b7280',
                                  marginLeft: '2px',
                                  fontSize: '10px'
                                }}>
                                  {teamAverageData.teamAverage.toFixed(0)}%
                                </span>
                              </div>
                              {teamAverageData.userScore > 0 && !selectedTeamMember && (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '10px'
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: '#3b82f6',
                                      flexShrink: 0
                                    }}
                                  />
                                  <span style={{
                                    color: isDark ? '#d1d5db' : '#374151',
                                    fontWeight: 500
                                  }}>
                                    Your Score
                                  </span>
                                  <span style={{
                                    color: isDark ? '#9ca3af' : '#6b7280',
                                    marginLeft: '2px',
                                    fontSize: '10px'
                                  }}>
                                    {teamAverageData.userScore.toFixed(0)}%
                                  </span>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </Card>
                    </motion.div>
                  </Col>
                )}

                {/* Active Projects Card */}
                <Col
                  xs={24}
                  sm={12}
                  lg={8}
                  xl={4}
                  style={{ display: 'flex' }}
                  className="goals-metrics-col"
                >
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    custom={3}
                    whileHover={cardHoverVariants.hover}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <Card
                      className="h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer"
                      hoverable
                      onClick={() => window.open('/dashboard/projects', '_blank')}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
                        borderRadius: '12px'
                      }}
                      bodyStyle={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '8px' }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 ${isDark
                          ? 'bg-orange-500/20 border border-orange-500/30'
                          : 'bg-orange-100 border border-orange-200'
                          }`}>
                          <Building2 className={`h-4 w-4 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                        </div>
                        <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                          {selectedTeamMember ? 'Project' : 'Total'}
                        </div>
                      </div>
                      {loading ? (
                        <>
                          {/* Custom Skeleton for Total Projects Card */}
                          <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '6px',
                              marginTop: '4px',
                              width: '100%',
                              maxWidth: '100%'
                            }}>
                              {/* Text Skeleton on Left */}
                              <div style={{ flex: '1 1 auto', minWidth: 0, maxWidth: '60%' }}>
                                <Skeleton.Input
                                  active
                                  size="small"
                                  style={{ width: '95px', maxWidth: '100%', height: '13px', marginBottom: '6px' }}
                                />
                                <Skeleton.Input
                                  active
                                  style={{ width: '40px', maxWidth: '100%', height: '24px' }}
                                />
                              </div>

                              {/* Donut Chart Skeleton on Right */}
                              <div style={{
                                width: '100px',
                                maxWidth: '40%',
                                height: '100px',
                                flexShrink: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}>
                                <Skeleton.Avatar
                                  active
                                  shape="circle"
                                  size={100}
                                  style={{ borderRadius: '50%', maxWidth: '100%', height: 'auto' }}
                                />
                              </div>
                            </div>

                            {/* Legend Skeleton */}
                            <div style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '8px',
                              width: '100%',
                              maxWidth: '100%',
                              marginTop: '8px'
                            }}>
                              {[1, 2].map((item) => (
                                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                  <Skeleton.Avatar active size={8} shape="circle" />
                                  <Skeleton.Input active size="small" style={{ width: '60px', maxWidth: '100%', height: '10px' }} />
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Total Projects Text and Concentric Donut Chart in Same Row */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px',
                            marginTop: '4px'
                          }}>
                            {/* Total Projects Text on Left */}
                            <div style={{ flexShrink: 0 }}>
                              <Statistic
                                title={<span style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>{selectedTeamMember ? 'Active Project' : 'Total Projects'}</span>}
                                value={selectedTeamMember ? 1 : (projectsData.totalProjects || 0)}
                                valueStyle={{ color: '#fb923c', fontSize: '24px', fontWeight: 'bold' }}
                                style={{ margin: 0 }}
                              />
                            </div>

                            {/* Donut Chart - Active vs Inactive on Right */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: '100px',
                              height: '100px',
                              flexShrink: 0
                            }}>
                              <Chart
                                type="donut"
                                height={100}
                                series={projectsChartData.series}
                                options={{
                                  chart: {
                                    type: 'donut',
                                    height: 100,
                                    toolbar: { show: false },
                                    animations: { enabled: true, speed: 800 }
                                  },
                                  labels: projectsChartData.categories,
                                  colors: projectsChartData.colors,
                                  plotOptions: {
                                    pie: {
                                      donut: {
                                        size: '70%',
                                        labels: {
                                          show: true,
                                          name: {
                                            show: false
                                          },
                                          value: {
                                            show: false
                                          },
                                          total: {
                                            show: true,
                                            label: 'Total',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: '#fb923c',
                                            formatter: function () {
                                              return projectsChartData.total || 0;
                                            }
                                          }
                                        }
                                      }
                                    }
                                  },
                                  dataLabels: { enabled: false },
                                  stroke: {
                                    show: true,
                                    width: 2,
                                    colors: [isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff']
                                  },
                                  tooltip: {
                                    enabled: true,
                                    theme: isDark ? 'dark' : 'light',
                                    custom: function ({ series, seriesIndex, w }) {
                                      const value = series[seriesIndex];
                                      const label = projectsChartData.categories[seriesIndex];
                                      const color = projectsChartData.colors[seriesIndex];
                                      return `
                                  <div style="
                                    background: ${isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff'};
                                    border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'};
                                    border-radius: 8px;
                                    padding: 8px 12px;
                                    font-size: 12px;
                                  ">
                                    <div style="display: flex; align-items: center; gap: 8px;">
                                      <div style="
                                        width: 12px;
                                        height: 12px;
                                        border-radius: 50%;
                                        background: ${color};
                                      "></div>
                                      <div>
                                        <div style="color: ${isDark ? '#9ca3af' : '#6b7280'}; font-size: 11px; margin-bottom: 2px;">${label}</div>
                                        <div style="color: ${color}; font-weight: 600; font-size: 14px;">${value}</div>
                                      </div>
                                    </div>
                                  </div>
                                `;
                                    }
                                  },
                                  legend: { show: false }
                                }}
                              />
                            </div>
                          </div>

                          {/* Legend Below - Horizontal Layout */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '12px',
                            width: '100%',
                            marginTop: '8px'
                          }}>
                            {projectsChartData.categories.map((category, index) => {
                              const value = projectsChartData.series[index] || 0;
                              const color = projectsChartData.colors[index];
                              return (
                                <div
                                  key={category}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '10px'
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: color,
                                      flexShrink: 0
                                    }}
                                  />
                                  <span style={{
                                    color: isDark ? '#d1d5db' : '#374151',
                                    fontWeight: 500
                                  }}>
                                    {category}
                                  </span>
                                  <span style={{
                                    color: isDark ? '#9ca3af' : '#6b7280',
                                    marginLeft: '2px',
                                    fontSize: '10px'
                                  }}>
                                    {value}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </Card>
                  </motion.div>
                </Col>

                {isS5 && (
                <>
                {/* GOAL AVG SCORE Card */}
                <Col
                  xs={24}
                  sm={12}
                  lg={8}
                  xl={4}
                  style={{ display: 'flex' }}
                  className="goals-metrics-col"
                >
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    custom={4}
                    whileHover={cardHoverVariants.hover}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <Card
                      className="h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
                      hoverable
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
                        borderRadius: '12px'
                      }}
                      bodyStyle={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '12px' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 ${isDark
                          ? 'bg-green-500/20 border border-green-500/30'
                          : 'bg-green-100 border border-green-200'
                          }`}>
                          <LineChart className={`h-4 w-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        </div>
                        <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">GOAL AVG SCORE</div>
                      </div>
                      {goalsTeamStatsLoading ? (
                        <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: '70px', maxWidth: '100%', height: '13px', marginBottom: '6px' }}
                          />
                          <Skeleton.Input
                            active
                            style={{ width: '90px', maxWidth: '100%', height: '24px' }}
                          />
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
                            <span style={{
                              color: '#10b981',
                              fontSize: '24px',
                              fontWeight: 'bold',
                              lineHeight: '1'
                            }}>
                              {goalsTeamStats.teamAverageScore !== null ? goalsTeamStats.teamAverageScore : '—'}
                            </span>
                            {goalsTeamStats.teamAverageScore !== null && (
                              <Badge
                                count="/10"
                                style={{
                                  backgroundColor: '#d1fae5',
                                  color: '#065f46',
                                  borderRadius: '8px',
                                  padding: '2px 8px',
                                  fontSize: '11px',
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                              <div style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: '#10b981',
                                flexShrink: 0
                              }} />
                              <span style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>Team average</span>
                            </div>
                            {goalsTeamStats.previousMonthChange !== null && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                                <TrendingDown className="h-3 w-3" style={{ color: '#ef4444' }} />
                                <span style={{ color: '#ef4444', fontWeight: 500 }}>
                                  {goalsTeamStats.previousMonthChange > 0 ? '+' : ''}{goalsTeamStats.previousMonthChange}% vs previous month
                                </span>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </Card>
                  </motion.div>
                </Col>

                {/* GOALS DONE Card */}
                <Col
                  xs={24}
                  sm={12}
                  lg={8}
                  xl={4}
                  style={{ display: 'flex' }}
                  className="goals-metrics-col"
                >
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    custom={5}
                    whileHover={cardHoverVariants.hover}
                    style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <Card
                      className="h-full transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
                      hoverable
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.95)' : '#ffffff',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
                        borderRadius: '12px'
                      }}
                      bodyStyle={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '12px' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 ${isDark
                          ? 'bg-green-500/20 border border-green-500/30'
                          : 'bg-green-100 border border-green-200'
                          }`}>
                          <CheckCircle className={`h-4 w-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        </div>
                        <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">GOALS DONE</div>
                      </div>
                      {goalsTeamStatsLoading ? (
                        <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                          <Skeleton.Input
                            active
                            size="small"
                            style={{ width: '70px', maxWidth: '100%', height: '13px', marginBottom: '6px' }}
                          />
                          <Skeleton.Input
                            active
                            style={{ width: '90px', maxWidth: '100%', height: '24px' }}
                          />
                        </div>
                      ) : (
                        <>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            marginTop: '4px'
                          }}>
                            <div style={{ flexShrink: 0 }}>
                              <Statistic
                                title={<span style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Completed</span>}
                                value={goalsTeamStats.completedGoalsCount}
                                valueStyle={{ color: '#10b981', fontSize: '24px', fontWeight: 'bold' }}
                                style={{ margin: 0 }}
                              />
                            </div>

                            {/* Circular Progress */}
                            <div style={{
                              width: '100px',
                              height: '100px',
                              flexShrink: 0,
                              position: 'relative'
                            }}>
                              <Chart
                                type="radialBar"
                                height={100}
                                series={[goalsTeamStats.totalGoalsCount > 0
                                  ? Math.round((goalsTeamStats.completedGoalsCount / goalsTeamStats.totalGoalsCount) * 100)
                                  : 0
                                ]}
                                options={{
                                  chart: {
                                    type: 'radialBar',
                                    height: 100,
                                    toolbar: { show: false },
                                    animations: { enabled: true, speed: 1000, easing: 'easeout' }
                                  },
                                  plotOptions: {
                                    radialBar: {
                                      startAngle: 0,
                                      endAngle: 360,
                                      hollow: {
                                        margin: 0,
                                        size: '70%',
                                        background: 'transparent',
                                        dropShadow: { enabled: false }
                                      },
                                      track: {
                                        background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6',
                                        strokeWidth: '100%',
                                        margin: 0
                                      },
                                      dataLabels: {
                                        show: true,
                                        name: { show: false },
                                        value: {
                                          show: true,
                                          fontSize: '20px',
                                          fontWeight: 700,
                                          color: '#10b981',
                                          offsetY: 0,
                                          fontFamily: 'inherit',
                                          formatter: function (val) {
                                            return val + '%';
                                          }
                                        }
                                      }
                                    }
                                  },
                                  fill: {
                                    type: 'gradient',
                                    gradient: {
                                      shade: 'dark',
                                      type: 'horizontal',
                                      shadeIntensity: 0.5,
                                      gradientToColors: ['#22c55e'],
                                      inverseColors: true,
                                      opacityFrom: 1,
                                      opacityTo: 0.9,
                                      stops: [0, 100],
                                      colorStops: [
                                        { offset: 0, color: '#10b981', opacity: 1 },
                                        { offset: 100, color: '#22c55e', opacity: 0.9 }
                                      ]
                                    }
                                  },
                                  colors: ['#10b981'],
                                  stroke: { lineCap: 'round', curve: 'smooth' }
                                }}
                              />
                            </div>
                          </div>

                          {/* Details Below */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '4px',
                            width: '100%',
                            marginTop: '8px',
                            fontSize: '10px'
                          }}>
                            <div style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: '#10b981',
                              flexShrink: 0
                            }} />
                            <span style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                              of {goalsTeamStats.totalGoalsCount} total
                            </span>
                          </div>
                        </>
                      )}
                    </Card>
                  </motion.div>
                </Col>
                </>
                )}
              </>
            );
          })()}
        </Row>

        {/* Goals Leaderboard Card - S5 only */}
        {isS5 && (
        <div className="mb-8">
          <GoalsLeaderboardCard />
        </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">

          {/* Project Overview Card */}
          <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center space-x-2 mb-0.5">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-orange-400" />
                    {selectedTeamMember
                      ? `${teamMembers.find(m => m._id === selectedTeamMember)?.firstName || 'Selected'} ${teamMembers.find(m => m._id === selectedTeamMember)?.lastName || 'Member'}'s Projects`
                      : (user?.role === 'org_admin' || user?.role === 'main_admin' || user?.permissions?.addProject) ? 'All Projects' : 'My Projects'
                    }
                  </h3>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Sort Toggle for Main Dashboard Projects */}
                <button
                  onClick={async () => {
                    const newOrder = projectsSortOrder === 'desc' ? 'asc' : 'desc';
                    console.log('Projects Sort Toggle Clicked:', {
                      currentOrder: projectsSortOrder,
                      newOrder,
                      willShow: newOrder === 'desc' ? 'High→Low' : 'Low→High'
                    });
                    setProjectsSortOrder(newOrder);
                    // Reset to page 1 when sorting changes
                    setProjectsPage(1);
                    // Trigger API call immediately with new sort order
                    if (user && startDate && endDate) {
                      await fetchProjectOverview(newOrder);
                    }
                  }}
                  className={`sort-toggle-btn px-2 py-0.5 text-xs rounded-md transition-colors flex items-center space-x-1 border ${isDark
                    ? 'bg-white/10 hover:bg-white/20 border-white/15'
                    : 'light-sort-btn'
                    }`}
                  title={`Sort by hours ${projectsSortOrder === 'desc' ? 'Ascending' : 'Descending'}`}
                >
                  <Clock className={`h-3 w-3 ${isDark ? 'text-white/70' : 'light-sort-icon'}`} />
                  <span className={isDark ? 'text-white/70' : 'light-sort-text'}>
                    {projectsSortOrder === 'desc' ? 'High→Low' : 'Low→High'}
                  </span>
                  <span className={isDark ? 'text-white/50' : 'light-sort-subtle'}>
                    {projectsSortOrder === 'desc' ? '↓' : '↑'}
                  </span>
                </button>

                {/* {!projectOverviewLoading && (
                  <div className="text-center flex items-center flex-col">
                    <div className="text-white font-bold text-sm">{allProjects.length}</div>
                  </div>
                )} */}
              </div>
            </div>


            {projectOverviewLoading ? (
              <div className="space-y-2 pr-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={`project-skeleton-${index}`} className="bg-gradient-to-r from-white/[0.08] to-white/[0.04] border border-white/20 rounded-lg p-3 hover:from-white/[0.12] hover:to-white/[0.08] hover:border-orange-500/30 hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-300 group animate-pulse">
                    {/* Responsive Row Layout - Matches actual structure */}
                    <div className="flex items-center justify-between gap-2 sm:gap-3">
                      {/* Project Name - Responsive flex */}
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${index % 3 === 0 ? 'bg-green-400' :
                          index % 3 === 1 ? 'bg-blue-400' :
                            'bg-gray-400'
                          }`}></div>
                        <h4 className="font-semibold text-white text-sm truncate">
                          <div className="h-4 bg-white/20 rounded animate-pulse w-3/4 sm:w-2/3 md:w-1/2"></div>
                        </h4>
                      </div>

                      {/* Time - Responsive width */}
                      <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0 w-16 sm:w-20 md:w-24">
                        <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-white font-medium text-sm">
                          <div className="h-4 bg-white/20 rounded animate-pulse w-8 sm:w-10 md:w-12"></div>
                        </span>
                      </div>

                      {/* Members Count - Responsive width */}
                      <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0 w-12 sm:w-14 md:w-16">
                        <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-white font-medium text-sm">
                          <div className="h-4 bg-white/20 rounded animate-pulse w-4 sm:w-6"></div>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : projectOverview.length === 0 ? (
              <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
                <div className="w-12 h-12 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Building2 className="h-6 w-6 text-gray-500" />
                </div>
                <div className="text-gray-400 text-sm font-medium">No projects found</div>
                <div className="text-gray-500 text-xs mt-1">Start tracking projects to see data here</div>
              </div>
            ) : (
              <div className="space-y-2 pr-2">
                {projectOverview.map((project, index) => (
                  <div key={project._id || project.projectId || project.id || index} className="bg-gradient-to-r from-white/[0.08] to-white/[0.04] border border-white/20 rounded-lg p-3 hover:from-white/[0.12] hover:to-white/[0.08] hover:border-orange-500/30 hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-300 group">
                    {/* Slim Row Layout */}
                    <div className="flex items-center justify-between gap-3">
                      {/* Project Name */}
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 animate-pulse ${project.isActive || project.status === 'active'
                          ? 'bg-green-400'
                          : project.status === 'completed'
                            ? 'bg-blue-400'
                            : 'bg-gray-400'
                          }`}></div>
                        <h4 className="font-semibold text-white text-sm truncate">{project.name || project.projectName}</h4>
                      </div>

                      {/* Time */}
                      <div className="flex items-center gap-1.5 w-24">
                        <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-white font-medium text-sm">
                          {project.timeSpent || project.formattedTime || '0h 0m'}
                        </span>
                      </div>

                      {/* Members Count */}
                      <div className="flex items-center gap-1.5 w-14 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMemberHoursPanel(project);
                        }}
                      >
                        <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-white font-medium text-sm">
                          {project.memberCount || 0}
                        </span>
                      </div>

                      {/* Productivity - Compact */}
                      {false && (
                        <>
                          <div className="flex items-center justify-center w-14">
                            <div className="relative w-9 h-9 flex-shrink-0">
                              <svg className="w-9 h-9 transform -rotate-90">
                                <circle
                                  cx="18"
                                  cy="18"
                                  r="14"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  fill="none"
                                  className="text-white/10"
                                />
                                <circle
                                  cx="18"
                                  cy="18"
                                  r="14"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  fill="none"
                                  strokeDasharray={`${2 * Math.PI * 14}`}
                                  strokeDashoffset={`${2 * Math.PI * 14 * (1 - (project.productivity || 0) / 100)}`}
                                  className={`transition-all duration-500 ${(project.productivity || 0) >= 70 ? 'text-emerald-400' :
                                    (project.productivity || 0) >= 40 ? 'text-amber-400' :
                                      'text-rose-400'
                                    }`}
                                  strokeLinecap="round"
                                />
                              </svg>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className={`text-[9px] font-bold tabular-nums ${(project.productivity || 0) >= 70 ? 'text-emerald-400' :
                                  (project.productivity || 0) >= 40 ? 'text-amber-400' :
                                    'text-rose-400'
                                  }`}>
                                  {project.productivity || 0}%
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Analytics Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openMemberHoursPanel(project);
                            }}
                            className="p-1.5 text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 rounded transition-all duration-300 border border-blue-500/20 hover:border-blue-500/40 flex-shrink-0"
                            title="View Project Analytics"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination for Projects */}
            {!projectOverviewLoading && projectOverview.length > 0 && projectOverviewPagination.pages > 1 && (
              <Pagination
                currentPage={projectsPage}
                totalPages={projectOverviewPagination.pages}
                onPageChange={setProjectsPage}
                totalItems={projectOverviewPagination.total}
                itemsPerPage={itemsPerPage}
              />
            )}
          </div>

          {/* Most Visited Card */}
          <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center space-x-2 mb-0.5">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Activity className="h-4 w-4 text-blue-400" />
                    Activity Tracking
                  </h3>
                  <div className="group relative">
                    <Lightbulb className="h-3 w-3 text-yellow-400 cursor-help" />
                    <div className="absolute left-0 top-6 bg-black/90 border border-white/20 rounded-lg p-3 text-xs text-white w-80 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-xl">
                      <div className="font-semibold text-yellow-400 mb-2">👁️ Activity Insights</div>
                      <ul className="space-y-1 text-gray-300">
                        <li>• <strong>Applications:</strong> Most frequently used desktop apps</li>
                        <li>• <strong>Websites:</strong> Most visited web domains and URLs</li>
                        <li>• <strong>Time Spent:</strong> Total active time on each platform</li>
                        <li>• <strong>Productivity:</strong> Helps identify focus patterns and distractions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {mostVisitedLoading ? (
              <div className="space-y-2 pr-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={`activity-skeleton-${index}`} className="flex items-center justify-between p-3 bg-gradient-to-r from-white/[0.08] to-white/[0.04] rounded-lg hover:from-white/[0.12] hover:to-white/[0.08] hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 border border-white/20 group animate-pulse">
                    {/* Exact structure match with actual activity items */}
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {/* Icon box - Exact match */}
                      <div className={`w-9 h-9 bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-blue-500/40 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 ${index % 2 === 0 ? 'from-blue-500/30 to-purple-500/30 border-blue-500/40' : 'from-purple-500/30 to-pink-500/30 border-purple-500/40'
                        }`}>
                        {index % 2 === 0 ? (
                          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                          </svg>
                        )}
                      </div>

                      {/* Name and type - Exact match */}
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium text-sm truncate">
                          <Skeleton.Input active size="small" style={{ width: '75%', height: 16, backgroundColor: 'transparent' }} />
                        </div>
                        <div className="text-gray-400 text-xs">
                          <Skeleton.Input active size="small" style={{ width: '60%', height: 12, backgroundColor: 'transparent' }} />
                        </div>
                      </div>
                    </div>

                    {/* Time display - Exact match */}
                    <div className="flex items-center gap-2 w-24">
                      <span className="text-white font-medium text-sm">
                        <Skeleton.Input active size="small" style={{ width: 60, height: 16, backgroundColor: 'transparent' }} />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : mostVisited.length === 0 ? (
              <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
                <div className="w-12 h-12 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Eye className="h-6 w-6 text-gray-500" />
                </div>
                <div className="text-gray-400 text-sm font-medium">
                  {startDate.toDateString() === new Date().toDateString() ? 'No activity data for today' : 'No activity data for selected period'}
                </div>
                <div className="text-gray-500 text-xs mt-1">
                  {startDate.toDateString() === new Date().toDateString()
                    ? 'Desktop tracking will show activity data here'
                    : 'No activity was tracked during this time period'
                  }
                </div>
                <div className="text-gray-600 text-xs mt-2">
                  Date: {formatDateForAPI(startDate, false)?.split('T')[0]}
                  {endDate && endDate.toDateString() !== startDate.toDateString() &&
                    ` to ${formatDateForAPI(endDate, true)?.split('T')[0]}`
                  }
                </div>
                {mostVisitedDebug && (
                  <div className="text-gray-600 text-xs mt-2 p-2 bg-gray-800/50 rounded">
                    {/* <div>Debug Info:</div>
                    <div>Screenshots: {mostVisitedDebug.summary?.totalScreenshots || 0}</div> */}
                    <div>Apps: {mostVisitedDebug.summary?.totalApps || 0}</div>
                    <div>Websites: {mostVisitedDebug.summary?.totalWebsites || 0}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2 pr-2">
                {mostVisited.map((item, index) => (
                  <div key={`${item.type}-${item.name}`} className="flex items-center justify-between p-3 bg-gradient-to-r from-white/[0.08] to-white/[0.04] rounded-lg hover:from-white/[0.12] hover:to-white/[0.08] hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 border border-white/20 group">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="w-9 h-9 bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-blue-500/40 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        {item.type === 'website' ? (
                          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium text-sm truncate">{item.name}</div>
                        <div className="text-gray-400 text-xs">
                          {item.type === 'website' ? 'Website' : 'App'}
                          {item.category && item.category !== 'unknown' && ` • ${item.category}`}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <div className="text-white text-sm font-semibold">
                        {formatTimeSafely(item.timeSpent, item.formattedTime)}
                      </div>
                      <div className="text-gray-500 text-xs">{item.sessions || 0} sessions</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Server-side Pagination for Activity Tracking */}
            {!mostVisitedLoading && mostVisitedTotal > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <div className="text-xs text-gray-400">
                  {mostVisitedTotal > 0 ? (
                    `Showing ${((mostVisitedPage - 1) * mostVisitedLimit) + 1} to ${Math.min(mostVisitedPage * mostVisitedLimit, mostVisitedTotal)} of ${mostVisitedTotal} items`
                  ) : (
                    `Page ${mostVisitedPage} • ${mostVisited?.length} items`
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setMostVisitedPage(prev => Math.max(1, prev - 1))}
                    disabled={mostVisitedPage === 1 || mostVisitedLoading}
                    className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, Math.ceil(mostVisitedTotal / mostVisitedLimit)) }, (_, idx) => {
                    const totalPages = Math.ceil(mostVisitedTotal / mostVisitedLimit);
                    const halfWindow = 2;
                    let start = Math.max(1, mostVisitedPage - halfWindow);
                    let end = Math.min(totalPages, start + 4);
                    start = Math.max(1, end - 4);
                    const pageNum = start + idx;
                    if (pageNum > totalPages) return null;
                    const isCurrent = pageNum === mostVisitedPage;
                    return (
                      <button
                        key={`mv-page-${pageNum}`}
                        onClick={() => setMostVisitedPage(pageNum)}
                        disabled={mostVisitedLoading}
                        className={`px-3 py-1 text-xs rounded-lg transition-colors ${isCurrent ? 'bg-white/20' : 'bg-white/10 hover:bg-white/20'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setMostVisitedPage(prev => prev + 1)}
                    disabled={!mostVisitedHasMore || mostVisitedLoading}
                    className="px-3 py-1 text-xs bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Team Analytics - Full Width Section */}
        <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-xl">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center space-x-2 mb-0.5">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Users className="h-4 w-4 text-green-400" />
                  Team Performance
                </h3>
                <Tooltip
                  title={
                    <div style={{ fontSize: 12 }}>
                      <div style={{ fontWeight: 600, color: '#60a5fa', marginBottom: 6 }}>👥 Team Analytics Guide</div>
                      <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <li>• <strong>Total Hours:</strong> Complete time tracked (includes idle)</li>
                        <li>• <strong>Idle:</strong> Time marked as idle by user</li>
                        <li>• <strong>Active:</strong> Pure working time (Total - Idle)</li>
                        <li>• <strong>Sessions:</strong> Number of separate work periods</li>
                        <li>• <strong>Tip:</strong> Higher active time indicates more focused work</li>
                      </ul>
                    </div>
                  }
                  overlayStyle={{ maxWidth: 320 }}
                >
                  <Info className="h-3 w-3 text-blue-400 cursor-help" />
                </Tooltip>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Sort Toggle for Team Performance */}
              <button
                onClick={async () => {
                  const newOrder = teamSortOrder === 'desc' ? 'asc' : 'desc';
                  console.log('Team Sort Toggle Clicked:', {
                    currentOrder: teamSortOrder,
                    newOrder,
                    willShow: newOrder === 'desc' ? 'High→Low' : 'Low→High',
                    teamAnalyticsLength: teamAnalytics.length
                  });
                  setTeamSortOrder(newOrder);
                  // Reset to page 1 when sorting changes
                  setTeamPage(1);
                  // Trigger API call immediately with new sort order
                  if (user && startDate && endDate) {
                    await fetchTeamAnalytics(newOrder);
                  }
                }}
                className={`sort-toggle-btn px-2 py-0.5 text-xs rounded-md transition-colors flex items-center space-x-1 border ${isDark
                  ? 'bg-white/10 hover:bg-white/20 border-white/15'
                  : 'light-sort-btn'
                  }`}
                title={`Sort by hours ${teamSortOrder === 'desc' ? 'Ascending' : 'Descending'}`}
              >
                <Clock className={`h-3 w-3 ${isDark ? 'text-white/70' : 'light-sort-icon'}`} />
                <span className={isDark ? 'text-white/70' : 'light-sort-text'}>
                  {teamSortOrder === 'desc' ? 'High→Low' : 'Low→High'}
                </span>
                <span className={isDark ? 'text-white/50' : 'light-sort-subtle'}>
                  {teamSortOrder === 'desc' ? '↓' : '↑'}
                </span>
              </button>

              {/* Download Button for Team Performance Export */}
              {ENABLE_TEAM_PERFORMANCE_EXPORT && (
                <button
                  onClick={exportTeamAnalyticsToExcel}
                  disabled={teamAnalyticsLoading || teamAnalytics.length === 0}
                  className={`px-2 py-0.5 text-xs rounded-md transition-colors flex items-center space-x-1 border ${isDark
                    ? 'bg-white/10 hover:bg-white/20 border-white/15 disabled:opacity-50 disabled:cursor-not-allowed'
                    : 'light-sort-btn disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  title="Download Team Performance Excel"
                >
                  <Download className={`h-3 w-3 ${isDark ? 'text-white/70' : 'light-sort-icon'}`} />
                  <span className={isDark ? 'text-white/70' : 'light-sort-text'}>Export</span>
                </button>
              )}

              {/* <div className="text-xs text-gray-400">{teamAnalytics.length} members</div> */}
            </div>
          </div>

          {teamAnalyticsLoading ? (
            <div className="w-full overflow-x-auto scrollbar-thin">
              {/* Header - Exact match with actual header */}
              <div className={`grid grid-cols-[1fr_80px_75px_80px_90px_130px] gap-3 px-3 py-2 border-b rounded-t-xl min-w-max ${isDark
                ? 'border-white/20 bg-white/5'
                : 'border-gray-200 bg-gray-50'
                }`}>
                <div className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Member</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Active</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Idle</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Total</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Sessions</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Actions</div>
              </div>

              {/* Rows - Exact match with actual row structure */}
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={`team-skeleton-${index}`} className={`grid grid-cols-[1fr_80px_75px_80px_90px_130px] gap-3 px-3 py-2.5 border rounded-lg transition-all duration-300 group min-w-max animate-pulse ${index === 0
                    ? isDark
                      ? 'bg-gradient-to-r from-blue-500/20 to-blue-600/10 border-blue-500/40 shadow-lg shadow-blue-500/20'
                      : 'bg-gradient-to-r from-blue-50 to-blue-100/50 border-blue-300 shadow-lg shadow-blue-200/50'
                    : isDark
                      ? 'bg-gradient-to-r from-white/[0.08] to-white/[0.04] border-white/20 hover:from-white/[0.12] hover:to-white/[0.08] hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/10'
                      : 'bg-white border-gray-200 hover:border-green-300 hover:shadow-md hover:shadow-green-100'
                    }`}>

                    {/* Member Info - Exact match with actual structure */}
                    <div className="flex items-center space-x-2 min-w-0">
                      <div className={`w-9 h-9 border rounded-lg flex items-center justify-center flex-shrink-0 ${index === 0
                        ? isDark
                          ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30'
                          : 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-300'
                        : isDark
                          ? 'bg-gradient-to-br from-gray-500/20 to-gray-600/20 border-white/20'
                          : 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300'
                        }`}>
                        <Skeleton.Input active size="small" style={{ width: 20, height: 12, backgroundColor: 'transparent' }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`font-semibold text-sm flex items-center flex-wrap gap-1.5 ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                          <span className="whitespace-nowrap">
                            <Skeleton.Input active size="small" style={{ width: '80%', height: 16, backgroundColor: 'transparent' }} />
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs rounded border flex-shrink-0 ${index === 0
                            ? isDark
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                              : 'bg-blue-100 text-blue-700 border-blue-300'
                            : isDark
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-green-100 text-green-700 border-green-300'
                            }`}>
                            <Skeleton.Input active size="small" style={{ width: 30, height: 10, backgroundColor: 'transparent' }} />
                          </span>
                        </div>
                        <div className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          <Skeleton.Input active size="small" style={{ width: '60%', height: 12, backgroundColor: 'transparent' }} />
                        </div>
                      </div>
                    </div>

                    {/* Active Time - Exact match */}
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className={`font-bold text-sm ${isDark ? 'text-green-400' : 'text-green-600'
                          }`}>
                          <Skeleton.Input active size="small" style={{ width: 50, height: 16, backgroundColor: 'transparent' }} />
                        </div>
                      </div>
                    </div>

                    {/* Idle Time - Exact match */}
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className={`font-bold text-sm ${isDark ? 'text-orange-400' : 'text-orange-600'
                          }`}>
                          <Skeleton.Input active size="small" style={{ width: 50, height: 16, backgroundColor: 'transparent' }} />
                        </div>
                      </div>
                    </div>

                    {/* Total Hours Worked - Exact match */}
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                          <Skeleton.Input active size="small" style={{ width: 50, height: 16, backgroundColor: 'transparent' }} />
                        </div>
                      </div>
                    </div>

                    {/* Sessions - Exact match */}
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                          <Skeleton.Input active size="small" style={{ width: 30, height: 16, backgroundColor: 'transparent' }} />
                        </div>
                      </div>
                    </div>

                    {/* Analytics Actions - Exact match */}
                    <div className="flex items-center justify-center gap-1.5">
                      <button className="p-1 text-blue-400 hover:text-blue-300 bg-gradient-to-br from-blue-500/20 to-blue-600/10 hover:from-blue-500/30 hover:to-blue-600/20 rounded transition-all duration-300 border border-blue-500/30 hover:border-blue-400/50 flex items-center justify-center">
                        <BarChart3 className="h-3 w-3" />
                      </button>
                      <button className="p-1 text-purple-400 hover:text-purple-300 bg-gradient-to-br from-purple-500/20 to-purple-600/10 hover:from-purple-500/30 hover:to-purple-600/20 rounded transition-all duration-300 border border-purple-500/30 hover:border-purple-400/50 flex items-center justify-center">
                        <Brain className="h-3 w-3" />
                      </button>
                      <button className="p-1 text-green-400 hover:text-green-300 bg-gradient-to-br from-green-500/20 to-green-600/10 hover:from-green-500/30 hover:to-green-600/20 rounded transition-all duration-300 border border-green-500/30 hover:border-green-400/50 flex items-center justify-center">
                        <Sparkles className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : teamAnalytics.length === 0 ? (
            <div className={`text-center py-12 rounded-xl border ${isDark
              ? 'bg-white/5 border-white/10'
              : 'bg-gray-50 border-gray-200'
              }`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 ${isDark ? 'bg-gray-500/20' : 'bg-gray-200'
                }`}>
                <Users className={`h-6 w-6 ${isDark ? 'text-gray-500' : 'text-gray-400'
                  }`} />
              </div>
              <div className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>No team data</div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'
                }`}>Team analytics will appear when members start tracking</div>
            </div>
          ) : (
            <div className="w-full overflow-x-auto scrollbar-thin">
              <div className={`grid grid-cols-[1fr_80px_75px_80px_90px_130px] gap-3 px-3 py-2 border-b rounded-t-xl min-w-max ${isDark
                ? 'border-white/20 bg-white/5'
                : 'border-gray-200 bg-gray-50'
                }`}>
                <div className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Member</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Active</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Idle</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Total</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Sessions</div>
                <div className={`text-xs font-medium uppercase tracking-wider text-center ${isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}>Actions</div>
              </div>
              <div className="space-y-2">
                {teamAnalytics.map((member, index) => (
                  <div key={member.userId} className={`grid grid-cols-[1fr_80px_75px_80px_90px_130px] gap-3 px-3 py-2.5 border rounded-lg transition-all duration-300 group min-w-max ${member.isCurrentUser || (selectedTeamMember && (member.userId === selectedTeamMember || member._id === selectedTeamMember))
                    ? isDark
                      ? 'bg-gradient-to-r from-blue-500/20 to-blue-600/10 border-blue-500/40 shadow-lg shadow-blue-500/20'
                      : 'bg-gradient-to-r from-blue-50 to-blue-100/50 border-blue-300 shadow-lg shadow-blue-200/50'
                    : isDark
                      ? 'bg-gradient-to-r from-white/[0.08] to-white/[0.04] border-white/20 hover:from-white/[0.12] hover:to-white/[0.08] hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/10'
                      : 'bg-white border-gray-200 hover:border-green-300 hover:shadow-md hover:shadow-green-100'
                    }`}>

                    {/* Member Info */}
                    <div className="flex items-center space-x-2 min-w-0">
                      <div className={`w-9 h-9 border rounded-lg flex items-center justify-center flex-shrink-0 ${member.isCurrentUser || (selectedTeamMember && (member.userId === selectedTeamMember || member._id === selectedTeamMember))
                        ? isDark
                          ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30'
                          : 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-300'
                        : isDark
                          ? 'bg-gradient-to-br from-gray-500/20 to-gray-600/20 border-white/20'
                          : 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300'
                        }`}>
                        <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                          {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`font-semibold text-sm flex items-center flex-wrap gap-1.5 ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                          <span className="whitespace-nowrap">{member.firstName} {member.lastName}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded border flex-shrink-0 ${member.isCurrentUser || (selectedTeamMember && (member.userId === selectedTeamMember || member._id === selectedTeamMember))
                            ? isDark
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                              : 'bg-blue-100 text-blue-700 border-blue-300'
                            : isDark
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-green-100 text-green-700 border-green-300'
                            }`}>
                            {member.relationshipLabel}
                          </span>

                          {/* Performance Badge - S5 only */}
                          {isS5 && (member.badgeIcon ? (
                            <Tooltip title={`${member.badge || 'Rank'}`}>
                              <div className="relative h-4 w-4">
                                <img
                                  src={member.badgeIcon}
                                  alt={member.badge}
                                  className="h-full w-full object-contain"
                                  onLoad={(e) => {
                                    e.target.style.opacity = '1';
                                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                  }}
                                  style={{ opacity: 0, transition: 'opacity 0.2s' }}
                                />
                                <Sparkles className="h-3.5 w-3.5 text-amber-500 absolute inset-0 hidden" />
                              </div>
                            </Tooltip>
                          ) : member.badge ? (
                            <Tooltip title={member.badge}>
                              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                            </Tooltip>
                          ) : null)}

                          {/* Streak Icon - S5 only */}
                          {isS5 && member.streak > 0 && (
                            <Tooltip title={`${member.streak} Month Streak`}>
                              <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-50 text-orange-600'
                                }`}>
                                {member.streakIcon ? (
                                  <img
                                    src={member.streakIcon}
                                    alt="streak"
                                    className="h-3 w-3 object-contain"
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                ) : (
                                  <Flame className="h-3 w-3 fill-current" />
                                )}
                                <span>{member.streak}</span>
                              </div>
                            </Tooltip>
                          )}
                        </div>
                        <div className={`text-xs flex items-center gap-2 truncate ${isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                          <span className="truncate">{member.position}</span>
                        </div>
                      </div>
                    </div>

                    {/* Active Time */}
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className={`font-bold text-sm ${isDark ? 'text-green-400' : 'text-green-600'
                          }`}>{member?.activeTime || '0h 0m'}</div>
                      </div>
                    </div>

                    {/* Idle Time */}
                    <div
                      className="flex items-center justify-center cursor-pointer group"
                      onClick={(e) => { e.stopPropagation(); openIdleLogs(member.userId, `${member.firstName} ${member.lastName}`.trim()); }}
                      title="View idle logs"
                      style={{ pointerEvents: 'all', cursor: 'pointer' }}
                    >
                      <div className={`font-bold text-sm transition-opacity underline decoration-dotted underline-offset-2 group-hover:opacity-75 ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                        {member?.idleTime || '0h 0m'}
                      </div>
                    </div>

                    {/* Total Hours Worked */}
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>{member?.hours || '0h 0m'}</div>
                      </div>
                    </div>

                    {/* Sessions */}
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <div className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>{member.sessions}</div>
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => openMemberAnalyticsPanel(member)}
                        className="p-1 text-blue-400 hover:text-blue-300 bg-gradient-to-br from-blue-500/20 to-blue-600/10 hover:from-blue-500/30 hover:to-blue-600/20 rounded transition-all duration-300 border border-blue-500/30 hover:border-blue-400/50 flex items-center justify-center"
                        title="Project Analytics"
                      >
                        <BarChart3 className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => {
                          const targetMemberId = (selectedTeamMember && (member.userId === selectedTeamMember || member._id === selectedTeamMember))
                            ? selectedTeamMember
                            : member.userId;
                          window.open(`/dashboard/daily-summary?memberId=${targetMemberId}&tab=hourly-summaries`, '_blank');
                        }}
                        className="p-1 text-purple-400 hover:text-purple-300 bg-gradient-to-br from-purple-500/20 to-purple-600/10 hover:from-purple-500/30 hover:to-purple-600/20 rounded transition-all duration-300 border border-purple-500/30 hover:border-purple-400/50 flex items-center justify-center"
                        title="AI Hourly Analysis"
                      >
                        <Brain className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => {
                          const targetMemberId = (selectedTeamMember && (member.userId === selectedTeamMember || member._id === selectedTeamMember))
                            ? selectedTeamMember
                            : member.userId;
                          window.open(`/dashboard/daily-summary?memberId=${targetMemberId}&tab=daily-analysis`, '_blank');
                        }}
                        className="p-1 text-green-400 hover:text-green-300 bg-gradient-to-br from-green-500/20 to-green-600/10 hover:from-green-500/30 hover:to-green-600/20 rounded transition-all duration-300 border border-green-500/30 hover:border-green-400/50 flex items-center justify-center"
                        title="AI Daily Analysis"
                      >
                        <Sparkles className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination for Team Performance */}
          {!teamAnalyticsLoading && teamAnalytics.length > 0 && teamAnalyticsPagination.pages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={teamPage}
                totalPages={teamAnalyticsPagination.pages}
                onPageChange={setTeamPage}
                totalItems={teamAnalyticsPagination.total}
                itemsPerPage={itemsPerPage}
              />
            </div>
          )}
        </div>

        {/* Helpful Tips Section */}
        {/* <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6 mb-6">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-blue-500/20 border border-blue-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <Lightbulb className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">💡 Dashboard Tips</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-300">
                <div>
                  <h4 className="font-medium text-white mb-1">🎯 Getting Started</h4>
                  <ul className="space-y-1 text-xs">
                    <li>• Use date filters to analyze specific time periods</li>
                    <li>• Hover over any section header for detailed explanations</li>
                    <li>• Monitor productivity scores to identify peak performance times</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-white mb-1">📊 Understanding Data</h4>
                  <ul className="space-y-1 text-xs">
                    <li>• Green indicators show active/positive metrics</li>
                    <li>• Yellow indicators show inactive/neutral status</li>
                    <li>• All times are tracked automatically via desktop app</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Project Overview Modal */}
        {isProjectModalOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-semibold text-white">
                      {selectedTeamMember
                        ? `${teamMembers.find(m => m._id === selectedTeamMember)?.firstName || 'Selected'} ${teamMembers.find(m => m._id === selectedTeamMember)?.lastName || 'Member'}'s Projects`
                        : user?.role === 'org_admin' || user?.role === 'main_admin' ? 'All Projects' : 'My Projects'
                      }
                    </h2>
                    {/* Sort Toggle for All Projects Modal */}
                    {(user?.role === 'org_admin' || user?.role === 'main_admin') && !selectedTeamMember && (
                      <button
                        onClick={async () => {
                          const newOrder = projectsSortOrder === 'desc' ? 'asc' : 'desc';
                          console.log('All Projects Modal Sort Toggle Clicked:', {
                            currentOrder: projectsSortOrder,
                            newOrder,
                            willShow: newOrder === 'desc' ? 'High→Low' : 'Low→High'
                          });
                          setProjectsSortOrder(newOrder);
                          // Reset to page 1 when sorting changes
                          setProjectsPage(1);
                          // Trigger API call immediately with new sort order
                          if (user && startDate && endDate) {
                            await fetchProjectOverview(newOrder);
                          }
                        }}
                        className={`sort-toggle-btn px-2 py-1 text-xs rounded-md transition-colors flex items-center space-x-1 border ${isDark
                          ? 'bg-white/10 hover:bg-white/20 border-white/15'
                          : 'light-sort-btn'
                          }`}
                        title={`Sort ${projectsSortOrder === 'desc' ? 'Ascending' : 'Descending'}`}
                      >
                        <Clock className={`h-3 w-3 ${isDark ? 'text-white/70' : 'light-sort-icon'}`} />
                        <span className={isDark ? 'text-white/70' : 'light-sort-text'}>
                          {projectsSortOrder === 'desc' ? 'Z→A' : 'A→Z'}
                        </span>
                        <span className={isDark ? 'text-white/50' : 'light-sort-subtle'}>
                          {projectsSortOrder === 'desc' ? '↓' : '↑'}
                        </span>
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    {sortedAllProjects.length} of {allProjects.length} projects • {allProjects.filter(p => p.isActive || p.status === 'active').length} active
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsProjectModalOpen(false);
                    setProjectsSearchTerm('');
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              {/* Search Input */}
              <div className="px-6 py-4 border-b border-white/10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search projects by name or description..."
                    value={projectsSearchTerm}
                    onChange={(e) => setProjectsSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
                  />
                  {projectsSearchTerm && (
                    <button
                      onClick={() => setProjectsSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {allProjects.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <FolderOpen className="h-8 w-8 text-gray-500" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">No Projects Found</h3>
                    <p className="text-gray-400">No projects are available at the moment.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {sortedAllProjects.map((project, index) => (
                      <div key={project._id || project.projectId || project.id || index} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all duration-200">
                        {/* Slim Row Layout */}
                        <div className="flex items-center justify-between gap-4">
                          {/* Project Name */}
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${project.isActive || project.status === 'active' ? 'bg-green-400' : project.status === 'completed' ? 'bg-blue-400' : 'bg-gray-400'}`}></div>
                            <h4 className="font-semibold text-white text-base truncate">{project.name || project.projectName}</h4>
                          </div>

                          {/* Time */}
                          <div className="flex items-center space-x-2 text-sm flex-shrink-0">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span className="text-white font-medium">
                              {project.timeSpent || project.formattedTime || '0h 0m'}
                            </span>
                          </div>

                          {/* Members Count */}
                          <div className="flex items-center space-x-2 text-sm flex-shrink-0">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span className="text-white font-medium">
                              {project.memberCount || 0}
                            </span>
                          </div>

                          {/* Analytics Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openMemberHoursPanel(project);
                            }}
                            className="p-2 text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg transition-all duration-300 group border border-blue-500/20 hover:border-blue-500/40 flex-shrink-0"
                            title="View Project Analytics"
                          >
                            <BarChart3 className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Activity Tracking Modal */}
        {isActivityModalOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div>
                  <h2 className="text-xl font-semibold text-white">Activity Tracking</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    Most used applications and websites • {mostVisited.length} items tracked
                  </p>
                </div>
                <button
                  onClick={() => setIsActivityModalOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {mostVisited.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Eye className="h-8 w-8 text-gray-500" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">No Activity Data</h3>
                    <p className="text-gray-400">
                      {startDate.toDateString() === new Date().toDateString()
                        ? 'Desktop tracking will show activity data here'
                        : 'No activity was tracked during this time period'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {mostVisited.map((item, index) => (
                      <div key={`${item.type}-${item.name}`} className="flex items-center justify-between p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-200 border border-white/10">
                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            {item.type === 'website' ? (
                              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                              </svg>
                            ) : (
                              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-white text-sm truncate">{item.name}</h3>
                            <p className="text-xs text-gray-400 capitalize">{item.type}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white font-semibold text-sm">
                            {formatTimeSafely(item.timeSpent, item.formattedTime)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.sessions || 0} sessions
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Server-Side Pagination Controls */}
                {mostVisitedTotal > 0 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                    <div className="text-sm text-gray-400">
                      {mostVisitedTotal > 0 ? (
                        `Showing ${((mostVisitedPage - 1) * mostVisitedLimit) + 1} to ${Math.min(mostVisitedPage * mostVisitedLimit, mostVisitedTotal)} of ${mostVisitedTotal} items`
                      ) : (
                        `Page ${mostVisitedPage} • ${mostVisited?.length} items`
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setMostVisitedPage(prev => Math.max(1, prev - 1))}
                        disabled={mostVisitedPage === 1 || mostVisitedLoading}
                        className="px-3 py-1 text-sm bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm bg-white/20 rounded-lg">
                        {mostVisitedPage}
                      </span>
                      <button
                        onClick={() => setMostVisitedPage(prev => prev + 1)}
                        disabled={!mostVisitedHasMore || mostVisitedLoading}
                        className="px-3 py-1 text-sm bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Team Members Modal */}
        {isTeamModalOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl max-w-6xl w-full max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-xl font-semibold text-white">All Team Members</h2>
                    {/* Sort Toggle for Team Performance Modal */}
                    <button
                      onClick={() => {
                        const newOrder = teamSortOrder === 'asc' ? 'desc' : 'asc';
                        setTeamSortOrder(newOrder);
                      }}
                      className={`sort-toggle-btn px-2 py-1 text-xs rounded-md transition-colors flex items-center space-x-1 border ${isDark
                        ? 'bg-white/10 hover:bg-white/20 border-white/15'
                        : 'light-sort-btn'
                        }`}
                      title={`Sort by hours ${teamSortOrder === 'desc' ? 'Ascending' : 'Descending'}`}
                    >
                      <Clock className={`h-3 w-3 ${isDark ? 'text-white/70' : 'light-sort-icon'}`} />
                      <span className={isDark ? 'text-white/70' : 'light-sort-text'}>
                        {teamSortOrder === 'desc' ? 'High→Low' : 'Low→High'}
                      </span>
                      <span className={isDark ? 'text-white/50' : 'light-sort-subtle'}>
                        {teamSortOrder === 'desc' ? '↓' : '↑'}
                      </span>
                    </button>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    {sortedTeamAnalytics.length} of {teamAnalytics.length} members • Performance overview
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsTeamModalOpen(false);
                    setTeamSearchTerm('');
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-all duration-200 group"
                >
                  <X className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
                </button>
              </div>

              {/* Search Input */}
              <div className="px-6 py-4 border-b border-white/10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search team members by name, email, or position..."
                    value={teamSearchTerm}
                    onChange={(e) => setTeamSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
                  />
                  {teamSearchTerm && (
                    <button
                      onClick={() => setTeamSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {teamAnalytics.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Users className="h-8 w-8 text-gray-500" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">No Team Data</h3>
                    <p className="text-gray-400">Team analytics will appear when members start tracking</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-6 px-4 py-3 border-b border-white/20 bg-white/5 rounded-t-xl">
                      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider flex-1">Member</div>
                      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider w-24 text-center">Hours</div>
                      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider w-20 text-center">Sessions</div>
                      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider w-20 text-center">Analytics</div>
                    </div>
                    {sortedTeamAnalytics.map((member, index) => (
                      <div key={member.userId} className={`flex items-center gap-6 px-4 py-4 border border-white/10 rounded-xl hover:bg-white/10 transition-all duration-200 ${member.isCurrentUser ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/5 border-blue-500/30' : 'bg-white/5'}`}>

                        {/* Member Info */}
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className={`w-12 h-12 border border-white/20 rounded-xl flex items-center justify-center flex-shrink-0 ${member.isCurrentUser ? 'bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30' : 'bg-gradient-to-br from-gray-500/20 to-gray-600/20'}`}>
                            <span className="text-sm font-bold text-white">
                              {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-white text-sm flex items-center flex-wrap gap-2">
                              <span className="whitespace-nowrap">{member.firstName} {member.lastName}</span>
                              <span className={`px-2 py-0.5 text-xs rounded-md border flex-shrink-0 ${member.isCurrentUser
                                ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                                : 'bg-green-500/20 text-green-400 border-green-500/30'
                                }`}>
                                {member.relationshipLabel}
                              </span>
                            </div>
                            <div className="text-gray-400 text-xs">{member.position}</div>
                            <div className="text-gray-500 text-xs">{member.email}</div>
                          </div>
                        </div>

                        {/* Hours Worked */}
                        <div className="flex items-center min-w-0 flex-shrink-0 w-20">
                          <div className="text-center">
                            <div className="text-white font-bold text-sm">{member.hours}</div>
                            <div className="text-gray-400 text-xs">worked</div>
                          </div>
                        </div>

                        {/* Sessions */}
                        <div className="flex items-center min-w-0 flex-shrink-0 w-20">
                          <div className="text-center">
                            <div className="text-white font-bold text-sm">{member.sessions}</div>
                            <div className="text-gray-400 text-xs">sessions</div>
                          </div>
                        </div>

                        {/* Analytics */}
                        <div className="flex items-center min-w-0 flex-shrink-0 w-20">
                          <button
                            onClick={() => openMemberAnalyticsPanel(member)}
                            className="p-2 text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 rounded-xl transition-all duration-300 group border border-blue-500/20 hover:border-blue-500/40 mx-auto"
                            title="View Member's Project Analytics"
                          >
                            <BarChart3 className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Member Hours Panel - Reusable Component */}
        <ProjectMemberHoursSidebar
          isOpen={showMemberHoursModal}
          onClose={closeMemberHoursPanel}
          project={selectedProjectForAnalysis}
          isAnimating={isMemberHoursPanelAnimating}
          onAnimatingChange={setIsMemberHoursPanelAnimating}
          dashboardStartDate={startDate}
          dashboardEndDate={endDate}
          selectedTeamMember={selectedTeamMember}
        />

        {/* Member Project Analytics Sidebar */}
        <MemberProjectAnalyticsSidebar
          isOpen={showMemberAnalyticsSidebar}
          onClose={closeMemberAnalyticsPanel}
          member={selectedMemberForAnalytics}
          isAnimating={isMemberAnalyticsPanelAnimating}
          dashboardStartDate={startDate}
          dashboardEndDate={endDate}
        />

        <IdleLogsModal
          isOpen={showIdleLogsModal}
          onClose={() => setShowIdleLogsModal(false)}
          userId={idleLogsTarget.userId}
          memberName={idleLogsTarget.memberName}
        />

      </div>
    </PageAnimationWrapper>
  );
};

export default Dashboard;